(() => {
/*
Stimulus 3.2.1
Copyright © 2023 Basecamp, LLC
 */ class $ffdf60ebf0144301$var$EventListener {
    constructor(eventTarget, eventName, eventOptions){
        this.eventTarget = eventTarget;
        this.eventName = eventName;
        this.eventOptions = eventOptions;
        this.unorderedBindings = new Set();
    }
    connect() {
        this.eventTarget.addEventListener(this.eventName, this, this.eventOptions);
    }
    disconnect() {
        this.eventTarget.removeEventListener(this.eventName, this, this.eventOptions);
    }
    bindingConnected(binding) {
        this.unorderedBindings.add(binding);
    }
    bindingDisconnected(binding) {
        this.unorderedBindings.delete(binding);
    }
    handleEvent(event) {
        const extendedEvent = $ffdf60ebf0144301$var$extendEvent(event);
        for (const binding of this.bindings){
            if (extendedEvent.immediatePropagationStopped) break;
            else binding.handleEvent(extendedEvent);
        }
    }
    hasBindings() {
        return this.unorderedBindings.size > 0;
    }
    get bindings() {
        return Array.from(this.unorderedBindings).sort((left, right)=>{
            const leftIndex = left.index, rightIndex = right.index;
            return leftIndex < rightIndex ? -1 : leftIndex > rightIndex ? 1 : 0;
        });
    }
}
function $ffdf60ebf0144301$var$extendEvent(event) {
    if ("immediatePropagationStopped" in event) return event;
    else {
        const { stopImmediatePropagation: stopImmediatePropagation } = event;
        return Object.assign(event, {
            immediatePropagationStopped: false,
            stopImmediatePropagation () {
                this.immediatePropagationStopped = true;
                stopImmediatePropagation.call(this);
            }
        });
    }
}
class $ffdf60ebf0144301$var$Dispatcher {
    constructor(application){
        this.application = application;
        this.eventListenerMaps = new Map();
        this.started = false;
    }
    start() {
        if (!this.started) {
            this.started = true;
            this.eventListeners.forEach((eventListener)=>eventListener.connect());
        }
    }
    stop() {
        if (this.started) {
            this.started = false;
            this.eventListeners.forEach((eventListener)=>eventListener.disconnect());
        }
    }
    get eventListeners() {
        return Array.from(this.eventListenerMaps.values()).reduce((listeners, map)=>listeners.concat(Array.from(map.values())), []);
    }
    bindingConnected(binding) {
        this.fetchEventListenerForBinding(binding).bindingConnected(binding);
    }
    bindingDisconnected(binding, clearEventListeners = false) {
        this.fetchEventListenerForBinding(binding).bindingDisconnected(binding);
        if (clearEventListeners) this.clearEventListenersForBinding(binding);
    }
    handleError(error, message, detail = {}) {
        this.application.handleError(error, `Error ${message}`, detail);
    }
    clearEventListenersForBinding(binding) {
        const eventListener = this.fetchEventListenerForBinding(binding);
        if (!eventListener.hasBindings()) {
            eventListener.disconnect();
            this.removeMappedEventListenerFor(binding);
        }
    }
    removeMappedEventListenerFor(binding) {
        const { eventTarget: eventTarget, eventName: eventName, eventOptions: eventOptions } = binding;
        const eventListenerMap = this.fetchEventListenerMapForEventTarget(eventTarget);
        const cacheKey = this.cacheKey(eventName, eventOptions);
        eventListenerMap.delete(cacheKey);
        if (eventListenerMap.size == 0) this.eventListenerMaps.delete(eventTarget);
    }
    fetchEventListenerForBinding(binding) {
        const { eventTarget: eventTarget, eventName: eventName, eventOptions: eventOptions } = binding;
        return this.fetchEventListener(eventTarget, eventName, eventOptions);
    }
    fetchEventListener(eventTarget, eventName, eventOptions) {
        const eventListenerMap = this.fetchEventListenerMapForEventTarget(eventTarget);
        const cacheKey = this.cacheKey(eventName, eventOptions);
        let eventListener = eventListenerMap.get(cacheKey);
        if (!eventListener) {
            eventListener = this.createEventListener(eventTarget, eventName, eventOptions);
            eventListenerMap.set(cacheKey, eventListener);
        }
        return eventListener;
    }
    createEventListener(eventTarget, eventName, eventOptions) {
        const eventListener = new $ffdf60ebf0144301$var$EventListener(eventTarget, eventName, eventOptions);
        if (this.started) eventListener.connect();
        return eventListener;
    }
    fetchEventListenerMapForEventTarget(eventTarget) {
        let eventListenerMap = this.eventListenerMaps.get(eventTarget);
        if (!eventListenerMap) {
            eventListenerMap = new Map();
            this.eventListenerMaps.set(eventTarget, eventListenerMap);
        }
        return eventListenerMap;
    }
    cacheKey(eventName, eventOptions) {
        const parts = [
            eventName
        ];
        Object.keys(eventOptions).sort().forEach((key)=>{
            parts.push(`${eventOptions[key] ? "" : "!"}${key}`);
        });
        return parts.join(":");
    }
}
const $ffdf60ebf0144301$var$defaultActionDescriptorFilters = {
    stop ({ event: event, value: value }) {
        if (value) event.stopPropagation();
        return true;
    },
    prevent ({ event: event, value: value }) {
        if (value) event.preventDefault();
        return true;
    },
    self ({ event: event, value: value, element: element }) {
        if (value) return element === event.target;
        else return true;
    }
};
const $ffdf60ebf0144301$var$descriptorPattern = /^(?:(?:([^.]+?)\+)?(.+?)(?:\.(.+?))?(?:@(window|document))?->)?(.+?)(?:#([^:]+?))(?::(.+))?$/;
function $ffdf60ebf0144301$var$parseActionDescriptorString(descriptorString) {
    const source = descriptorString.trim();
    const matches = source.match($ffdf60ebf0144301$var$descriptorPattern) || [];
    let eventName = matches[2];
    let keyFilter = matches[3];
    if (keyFilter && ![
        "keydown",
        "keyup",
        "keypress"
    ].includes(eventName)) {
        eventName += `.${keyFilter}`;
        keyFilter = "";
    }
    return {
        eventTarget: $ffdf60ebf0144301$var$parseEventTarget(matches[4]),
        eventName: eventName,
        eventOptions: matches[7] ? $ffdf60ebf0144301$var$parseEventOptions(matches[7]) : {},
        identifier: matches[5],
        methodName: matches[6],
        keyFilter: matches[1] || keyFilter
    };
}
function $ffdf60ebf0144301$var$parseEventTarget(eventTargetName) {
    if (eventTargetName == "window") return window;
    else if (eventTargetName == "document") return document;
}
function $ffdf60ebf0144301$var$parseEventOptions(eventOptions) {
    return eventOptions.split(":").reduce((options, token)=>Object.assign(options, {
            [token.replace(/^!/, "")]: !/^!/.test(token)
        }), {});
}
function $ffdf60ebf0144301$var$stringifyEventTarget(eventTarget) {
    if (eventTarget == window) return "window";
    else if (eventTarget == document) return "document";
}
function $ffdf60ebf0144301$var$camelize(value) {
    return value.replace(/(?:[_-])([a-z0-9])/g, (_, char)=>char.toUpperCase());
}
function $ffdf60ebf0144301$var$namespaceCamelize(value) {
    return $ffdf60ebf0144301$var$camelize(value.replace(/--/g, "-").replace(/__/g, "_"));
}
function $ffdf60ebf0144301$var$capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
function $ffdf60ebf0144301$var$dasherize(value) {
    return value.replace(/([A-Z])/g, (_, char)=>`-${char.toLowerCase()}`);
}
function $ffdf60ebf0144301$var$tokenize(value) {
    return value.match(/[^\s]+/g) || [];
}
function $ffdf60ebf0144301$var$isSomething(object) {
    return object !== null && object !== undefined;
}
function $ffdf60ebf0144301$var$hasProperty(object, property) {
    return Object.prototype.hasOwnProperty.call(object, property);
}
const $ffdf60ebf0144301$var$allModifiers = [
    "meta",
    "ctrl",
    "alt",
    "shift"
];
class $ffdf60ebf0144301$var$Action {
    constructor(element, index, descriptor, schema){
        this.element = element;
        this.index = index;
        this.eventTarget = descriptor.eventTarget || element;
        this.eventName = descriptor.eventName || $ffdf60ebf0144301$var$getDefaultEventNameForElement(element) || $ffdf60ebf0144301$var$error("missing event name");
        this.eventOptions = descriptor.eventOptions || {};
        this.identifier = descriptor.identifier || $ffdf60ebf0144301$var$error("missing identifier");
        this.methodName = descriptor.methodName || $ffdf60ebf0144301$var$error("missing method name");
        this.keyFilter = descriptor.keyFilter || "";
        this.schema = schema;
    }
    static forToken(token, schema) {
        return new this(token.element, token.index, $ffdf60ebf0144301$var$parseActionDescriptorString(token.content), schema);
    }
    toString() {
        const eventFilter = this.keyFilter ? `.${this.keyFilter}` : "";
        const eventTarget = this.eventTargetName ? `@${this.eventTargetName}` : "";
        return `${this.eventName}${eventFilter}${eventTarget}->${this.identifier}#${this.methodName}`;
    }
    shouldIgnoreKeyboardEvent(event) {
        if (!this.keyFilter) return false;
        const filters = this.keyFilter.split("+");
        if (this.keyFilterDissatisfied(event, filters)) return true;
        const standardFilter = filters.filter((key)=>!$ffdf60ebf0144301$var$allModifiers.includes(key))[0];
        if (!standardFilter) return false;
        if (!$ffdf60ebf0144301$var$hasProperty(this.keyMappings, standardFilter)) $ffdf60ebf0144301$var$error(`contains unknown key filter: ${this.keyFilter}`);
        return this.keyMappings[standardFilter].toLowerCase() !== event.key.toLowerCase();
    }
    shouldIgnoreMouseEvent(event) {
        if (!this.keyFilter) return false;
        const filters = [
            this.keyFilter
        ];
        if (this.keyFilterDissatisfied(event, filters)) return true;
        return false;
    }
    get params() {
        const params = {};
        const pattern = new RegExp(`^data-${this.identifier}-(.+)-param$`, "i");
        for (const { name: name, value: value } of Array.from(this.element.attributes)){
            const match = name.match(pattern);
            const key = match && match[1];
            if (key) params[$ffdf60ebf0144301$var$camelize(key)] = $ffdf60ebf0144301$var$typecast(value);
        }
        return params;
    }
    get eventTargetName() {
        return $ffdf60ebf0144301$var$stringifyEventTarget(this.eventTarget);
    }
    get keyMappings() {
        return this.schema.keyMappings;
    }
    keyFilterDissatisfied(event, filters) {
        const [meta, ctrl, alt, shift] = $ffdf60ebf0144301$var$allModifiers.map((modifier)=>filters.includes(modifier));
        return event.metaKey !== meta || event.ctrlKey !== ctrl || event.altKey !== alt || event.shiftKey !== shift;
    }
}
const $ffdf60ebf0144301$var$defaultEventNames = {
    a: ()=>"click",
    button: ()=>"click",
    form: ()=>"submit",
    details: ()=>"toggle",
    input: (e)=>e.getAttribute("type") == "submit" ? "click" : "input",
    select: ()=>"change",
    textarea: ()=>"input"
};
function $ffdf60ebf0144301$var$getDefaultEventNameForElement(element) {
    const tagName = element.tagName.toLowerCase();
    if (tagName in $ffdf60ebf0144301$var$defaultEventNames) return $ffdf60ebf0144301$var$defaultEventNames[tagName](element);
}
function $ffdf60ebf0144301$var$error(message) {
    throw new Error(message);
}
function $ffdf60ebf0144301$var$typecast(value) {
    try {
        return JSON.parse(value);
    } catch (o_O) {
        return value;
    }
}
class $ffdf60ebf0144301$var$Binding {
    constructor(context, action){
        this.context = context;
        this.action = action;
    }
    get index() {
        return this.action.index;
    }
    get eventTarget() {
        return this.action.eventTarget;
    }
    get eventOptions() {
        return this.action.eventOptions;
    }
    get identifier() {
        return this.context.identifier;
    }
    handleEvent(event) {
        const actionEvent = this.prepareActionEvent(event);
        if (this.willBeInvokedByEvent(event) && this.applyEventModifiers(actionEvent)) this.invokeWithEvent(actionEvent);
    }
    get eventName() {
        return this.action.eventName;
    }
    get method() {
        const method = this.controller[this.methodName];
        if (typeof method == "function") return method;
        throw new Error(`Action "${this.action}" references undefined method "${this.methodName}"`);
    }
    applyEventModifiers(event) {
        const { element: element } = this.action;
        const { actionDescriptorFilters: actionDescriptorFilters } = this.context.application;
        const { controller: controller } = this.context;
        let passes = true;
        for (const [name, value] of Object.entries(this.eventOptions)){
            if (name in actionDescriptorFilters) {
                const filter = actionDescriptorFilters[name];
                passes = passes && filter({
                    name: name,
                    value: value,
                    event: event,
                    element: element,
                    controller: controller
                });
            } else continue;
        }
        return passes;
    }
    prepareActionEvent(event) {
        return Object.assign(event, {
            params: this.action.params
        });
    }
    invokeWithEvent(event) {
        const { target: target, currentTarget: currentTarget } = event;
        try {
            this.method.call(this.controller, event);
            this.context.logDebugActivity(this.methodName, {
                event: event,
                target: target,
                currentTarget: currentTarget,
                action: this.methodName
            });
        } catch (error) {
            const { identifier: identifier, controller: controller, element: element, index: index } = this;
            const detail = {
                identifier: identifier,
                controller: controller,
                element: element,
                index: index,
                event: event
            };
            this.context.handleError(error, `invoking action "${this.action}"`, detail);
        }
    }
    willBeInvokedByEvent(event) {
        const eventTarget = event.target;
        if (event instanceof KeyboardEvent && this.action.shouldIgnoreKeyboardEvent(event)) return false;
        if (event instanceof MouseEvent && this.action.shouldIgnoreMouseEvent(event)) return false;
        if (this.element === eventTarget) return true;
        else if (eventTarget instanceof Element && this.element.contains(eventTarget)) return this.scope.containsElement(eventTarget);
        else return this.scope.containsElement(this.action.element);
    }
    get controller() {
        return this.context.controller;
    }
    get methodName() {
        return this.action.methodName;
    }
    get element() {
        return this.scope.element;
    }
    get scope() {
        return this.context.scope;
    }
}
class $ffdf60ebf0144301$export$12e07a2a9f87578a {
    constructor(element, delegate){
        this.mutationObserverInit = {
            attributes: true,
            childList: true,
            subtree: true
        };
        this.element = element;
        this.started = false;
        this.delegate = delegate;
        this.elements = new Set();
        this.mutationObserver = new MutationObserver((mutations)=>this.processMutations(mutations));
    }
    start() {
        if (!this.started) {
            this.started = true;
            this.mutationObserver.observe(this.element, this.mutationObserverInit);
            this.refresh();
        }
    }
    pause(callback) {
        if (this.started) {
            this.mutationObserver.disconnect();
            this.started = false;
        }
        callback();
        if (!this.started) {
            this.mutationObserver.observe(this.element, this.mutationObserverInit);
            this.started = true;
        }
    }
    stop() {
        if (this.started) {
            this.mutationObserver.takeRecords();
            this.mutationObserver.disconnect();
            this.started = false;
        }
    }
    refresh() {
        if (this.started) {
            const matches = new Set(this.matchElementsInTree());
            for (const element of Array.from(this.elements))if (!matches.has(element)) this.removeElement(element);
            for (const element of Array.from(matches))this.addElement(element);
        }
    }
    processMutations(mutations) {
        if (this.started) for (const mutation of mutations)this.processMutation(mutation);
    }
    processMutation(mutation) {
        if (mutation.type == "attributes") this.processAttributeChange(mutation.target, mutation.attributeName);
        else if (mutation.type == "childList") {
            this.processRemovedNodes(mutation.removedNodes);
            this.processAddedNodes(mutation.addedNodes);
        }
    }
    processAttributeChange(element, attributeName) {
        if (this.elements.has(element)) {
            if (this.delegate.elementAttributeChanged && this.matchElement(element)) this.delegate.elementAttributeChanged(element, attributeName);
            else this.removeElement(element);
        } else if (this.matchElement(element)) this.addElement(element);
    }
    processRemovedNodes(nodes) {
        for (const node of Array.from(nodes)){
            const element = this.elementFromNode(node);
            if (element) this.processTree(element, this.removeElement);
        }
    }
    processAddedNodes(nodes) {
        for (const node of Array.from(nodes)){
            const element = this.elementFromNode(node);
            if (element && this.elementIsActive(element)) this.processTree(element, this.addElement);
        }
    }
    matchElement(element) {
        return this.delegate.matchElement(element);
    }
    matchElementsInTree(tree = this.element) {
        return this.delegate.matchElementsInTree(tree);
    }
    processTree(tree, processor) {
        for (const element of this.matchElementsInTree(tree))processor.call(this, element);
    }
    elementFromNode(node) {
        if (node.nodeType == Node.ELEMENT_NODE) return node;
    }
    elementIsActive(element) {
        if (element.isConnected != this.element.isConnected) return false;
        else return this.element.contains(element);
    }
    addElement(element) {
        if (!this.elements.has(element)) {
            if (this.elementIsActive(element)) {
                this.elements.add(element);
                if (this.delegate.elementMatched) this.delegate.elementMatched(element);
            }
        }
    }
    removeElement(element) {
        if (this.elements.has(element)) {
            this.elements.delete(element);
            if (this.delegate.elementUnmatched) this.delegate.elementUnmatched(element);
        }
    }
}
class $ffdf60ebf0144301$export$fe68319d413d05cb {
    constructor(element, attributeName, delegate){
        this.attributeName = attributeName;
        this.delegate = delegate;
        this.elementObserver = new $ffdf60ebf0144301$export$12e07a2a9f87578a(element, this);
    }
    get element() {
        return this.elementObserver.element;
    }
    get selector() {
        return `[${this.attributeName}]`;
    }
    start() {
        this.elementObserver.start();
    }
    pause(callback) {
        this.elementObserver.pause(callback);
    }
    stop() {
        this.elementObserver.stop();
    }
    refresh() {
        this.elementObserver.refresh();
    }
    get started() {
        return this.elementObserver.started;
    }
    matchElement(element) {
        return element.hasAttribute(this.attributeName);
    }
    matchElementsInTree(tree) {
        const match = this.matchElement(tree) ? [
            tree
        ] : [];
        const matches = Array.from(tree.querySelectorAll(this.selector));
        return match.concat(matches);
    }
    elementMatched(element) {
        if (this.delegate.elementMatchedAttribute) this.delegate.elementMatchedAttribute(element, this.attributeName);
    }
    elementUnmatched(element) {
        if (this.delegate.elementUnmatchedAttribute) this.delegate.elementUnmatchedAttribute(element, this.attributeName);
    }
    elementAttributeChanged(element, attributeName) {
        if (this.delegate.elementAttributeValueChanged && this.attributeName == attributeName) this.delegate.elementAttributeValueChanged(element, attributeName);
    }
}
function $ffdf60ebf0144301$export$e16d8520af44a096(map, key, value) {
    $ffdf60ebf0144301$export$e7aa7bc5c1b3cfb3(map, key).add(value);
}
function $ffdf60ebf0144301$export$1d2f21e549771e67(map, key, value) {
    $ffdf60ebf0144301$export$e7aa7bc5c1b3cfb3(map, key).delete(value);
    $ffdf60ebf0144301$export$d6cf75919f12ca5a(map, key);
}
function $ffdf60ebf0144301$export$e7aa7bc5c1b3cfb3(map, key) {
    let values = map.get(key);
    if (!values) {
        values = new Set();
        map.set(key, values);
    }
    return values;
}
function $ffdf60ebf0144301$export$d6cf75919f12ca5a(map, key) {
    const values = map.get(key);
    if (values != null && values.size == 0) map.delete(key);
}
class $ffdf60ebf0144301$export$8bd4ad37b76db6ba {
    constructor(){
        this.valuesByKey = new Map();
    }
    get keys() {
        return Array.from(this.valuesByKey.keys());
    }
    get values() {
        const sets = Array.from(this.valuesByKey.values());
        return sets.reduce((values, set)=>values.concat(Array.from(set)), []);
    }
    get size() {
        const sets = Array.from(this.valuesByKey.values());
        return sets.reduce((size, set)=>size + set.size, 0);
    }
    add(key, value) {
        $ffdf60ebf0144301$export$e16d8520af44a096(this.valuesByKey, key, value);
    }
    delete(key, value) {
        $ffdf60ebf0144301$export$1d2f21e549771e67(this.valuesByKey, key, value);
    }
    has(key, value) {
        const values = this.valuesByKey.get(key);
        return values != null && values.has(value);
    }
    hasKey(key) {
        return this.valuesByKey.has(key);
    }
    hasValue(value) {
        const sets = Array.from(this.valuesByKey.values());
        return sets.some((set)=>set.has(value));
    }
    getValuesForKey(key) {
        const values = this.valuesByKey.get(key);
        return values ? Array.from(values) : [];
    }
    getKeysForValue(value) {
        return Array.from(this.valuesByKey).filter(([_key, values])=>values.has(value)).map(([key, _values])=>key);
    }
}
class $ffdf60ebf0144301$export$a7c3d2c2552a674d extends $ffdf60ebf0144301$export$8bd4ad37b76db6ba {
    constructor(){
        super();
        this.keysByValue = new Map();
    }
    get values() {
        return Array.from(this.keysByValue.keys());
    }
    add(key, value) {
        super.add(key, value);
        $ffdf60ebf0144301$export$e16d8520af44a096(this.keysByValue, value, key);
    }
    delete(key, value) {
        super.delete(key, value);
        $ffdf60ebf0144301$export$1d2f21e549771e67(this.keysByValue, value, key);
    }
    hasValue(value) {
        return this.keysByValue.has(value);
    }
    getKeysForValue(value) {
        const set = this.keysByValue.get(value);
        return set ? Array.from(set) : [];
    }
}
class $ffdf60ebf0144301$export$116e0b48509995b {
    constructor(element, selector, delegate, details){
        this._selector = selector;
        this.details = details;
        this.elementObserver = new $ffdf60ebf0144301$export$12e07a2a9f87578a(element, this);
        this.delegate = delegate;
        this.matchesByElement = new $ffdf60ebf0144301$export$8bd4ad37b76db6ba();
    }
    get started() {
        return this.elementObserver.started;
    }
    get selector() {
        return this._selector;
    }
    set selector(selector) {
        this._selector = selector;
        this.refresh();
    }
    start() {
        this.elementObserver.start();
    }
    pause(callback) {
        this.elementObserver.pause(callback);
    }
    stop() {
        this.elementObserver.stop();
    }
    refresh() {
        this.elementObserver.refresh();
    }
    get element() {
        return this.elementObserver.element;
    }
    matchElement(element) {
        const { selector: selector } = this;
        if (selector) {
            const matches = element.matches(selector);
            if (this.delegate.selectorMatchElement) return matches && this.delegate.selectorMatchElement(element, this.details);
            return matches;
        } else return false;
    }
    matchElementsInTree(tree) {
        const { selector: selector } = this;
        if (selector) {
            const match = this.matchElement(tree) ? [
                tree
            ] : [];
            const matches = Array.from(tree.querySelectorAll(selector)).filter((match)=>this.matchElement(match));
            return match.concat(matches);
        } else return [];
    }
    elementMatched(element) {
        const { selector: selector } = this;
        if (selector) this.selectorMatched(element, selector);
    }
    elementUnmatched(element) {
        const selectors = this.matchesByElement.getKeysForValue(element);
        for (const selector of selectors)this.selectorUnmatched(element, selector);
    }
    elementAttributeChanged(element, _attributeName) {
        const { selector: selector } = this;
        if (selector) {
            const matches = this.matchElement(element);
            const matchedBefore = this.matchesByElement.has(selector, element);
            if (matches && !matchedBefore) this.selectorMatched(element, selector);
            else if (!matches && matchedBefore) this.selectorUnmatched(element, selector);
        }
    }
    selectorMatched(element, selector) {
        this.delegate.selectorMatched(element, selector, this.details);
        this.matchesByElement.add(selector, element);
    }
    selectorUnmatched(element, selector) {
        this.delegate.selectorUnmatched(element, selector, this.details);
        this.matchesByElement.delete(selector, element);
    }
}
class $ffdf60ebf0144301$export$da226bebbf7f6caa {
    constructor(element, delegate){
        this.element = element;
        this.delegate = delegate;
        this.started = false;
        this.stringMap = new Map();
        this.mutationObserver = new MutationObserver((mutations)=>this.processMutations(mutations));
    }
    start() {
        if (!this.started) {
            this.started = true;
            this.mutationObserver.observe(this.element, {
                attributes: true,
                attributeOldValue: true
            });
            this.refresh();
        }
    }
    stop() {
        if (this.started) {
            this.mutationObserver.takeRecords();
            this.mutationObserver.disconnect();
            this.started = false;
        }
    }
    refresh() {
        if (this.started) for (const attributeName of this.knownAttributeNames)this.refreshAttribute(attributeName, null);
    }
    processMutations(mutations) {
        if (this.started) for (const mutation of mutations)this.processMutation(mutation);
    }
    processMutation(mutation) {
        const attributeName = mutation.attributeName;
        if (attributeName) this.refreshAttribute(attributeName, mutation.oldValue);
    }
    refreshAttribute(attributeName, oldValue) {
        const key = this.delegate.getStringMapKeyForAttribute(attributeName);
        if (key != null) {
            if (!this.stringMap.has(attributeName)) this.stringMapKeyAdded(key, attributeName);
            const value = this.element.getAttribute(attributeName);
            if (this.stringMap.get(attributeName) != value) this.stringMapValueChanged(value, key, oldValue);
            if (value == null) {
                const oldValue = this.stringMap.get(attributeName);
                this.stringMap.delete(attributeName);
                if (oldValue) this.stringMapKeyRemoved(key, attributeName, oldValue);
            } else this.stringMap.set(attributeName, value);
        }
    }
    stringMapKeyAdded(key, attributeName) {
        if (this.delegate.stringMapKeyAdded) this.delegate.stringMapKeyAdded(key, attributeName);
    }
    stringMapValueChanged(value, key, oldValue) {
        if (this.delegate.stringMapValueChanged) this.delegate.stringMapValueChanged(value, key, oldValue);
    }
    stringMapKeyRemoved(key, attributeName, oldValue) {
        if (this.delegate.stringMapKeyRemoved) this.delegate.stringMapKeyRemoved(key, attributeName, oldValue);
    }
    get knownAttributeNames() {
        return Array.from(new Set(this.currentAttributeNames.concat(this.recordedAttributeNames)));
    }
    get currentAttributeNames() {
        return Array.from(this.element.attributes).map((attribute)=>attribute.name);
    }
    get recordedAttributeNames() {
        return Array.from(this.stringMap.keys());
    }
}
class $ffdf60ebf0144301$export$3aa21c2f03ef6b3b {
    constructor(element, attributeName, delegate){
        this.attributeObserver = new $ffdf60ebf0144301$export$fe68319d413d05cb(element, attributeName, this);
        this.delegate = delegate;
        this.tokensByElement = new $ffdf60ebf0144301$export$8bd4ad37b76db6ba();
    }
    get started() {
        return this.attributeObserver.started;
    }
    start() {
        this.attributeObserver.start();
    }
    pause(callback) {
        this.attributeObserver.pause(callback);
    }
    stop() {
        this.attributeObserver.stop();
    }
    refresh() {
        this.attributeObserver.refresh();
    }
    get element() {
        return this.attributeObserver.element;
    }
    get attributeName() {
        return this.attributeObserver.attributeName;
    }
    elementMatchedAttribute(element) {
        this.tokensMatched(this.readTokensForElement(element));
    }
    elementAttributeValueChanged(element) {
        const [unmatchedTokens, matchedTokens] = this.refreshTokensForElement(element);
        this.tokensUnmatched(unmatchedTokens);
        this.tokensMatched(matchedTokens);
    }
    elementUnmatchedAttribute(element) {
        this.tokensUnmatched(this.tokensByElement.getValuesForKey(element));
    }
    tokensMatched(tokens) {
        tokens.forEach((token)=>this.tokenMatched(token));
    }
    tokensUnmatched(tokens) {
        tokens.forEach((token)=>this.tokenUnmatched(token));
    }
    tokenMatched(token) {
        this.delegate.tokenMatched(token);
        this.tokensByElement.add(token.element, token);
    }
    tokenUnmatched(token) {
        this.delegate.tokenUnmatched(token);
        this.tokensByElement.delete(token.element, token);
    }
    refreshTokensForElement(element) {
        const previousTokens = this.tokensByElement.getValuesForKey(element);
        const currentTokens = this.readTokensForElement(element);
        const firstDifferingIndex = $ffdf60ebf0144301$var$zip(previousTokens, currentTokens).findIndex(([previousToken, currentToken])=>!$ffdf60ebf0144301$var$tokensAreEqual(previousToken, currentToken));
        if (firstDifferingIndex == -1) return [
            [],
            []
        ];
        else return [
            previousTokens.slice(firstDifferingIndex),
            currentTokens.slice(firstDifferingIndex)
        ];
    }
    readTokensForElement(element) {
        const attributeName = this.attributeName;
        const tokenString = element.getAttribute(attributeName) || "";
        return $ffdf60ebf0144301$var$parseTokenString(tokenString, element, attributeName);
    }
}
function $ffdf60ebf0144301$var$parseTokenString(tokenString, element, attributeName) {
    return tokenString.trim().split(/\s+/).filter((content)=>content.length).map((content, index)=>({
            element: element,
            attributeName: attributeName,
            content: content,
            index: index
        }));
}
function $ffdf60ebf0144301$var$zip(left, right) {
    const length = Math.max(left.length, right.length);
    return Array.from({
        length: length
    }, (_, index)=>[
            left[index],
            right[index]
        ]);
}
function $ffdf60ebf0144301$var$tokensAreEqual(left, right) {
    return left && right && left.index == right.index && left.content == right.content;
}
class $ffdf60ebf0144301$export$d27fa4c7330a452e {
    constructor(element, attributeName, delegate){
        this.tokenListObserver = new $ffdf60ebf0144301$export$3aa21c2f03ef6b3b(element, attributeName, this);
        this.delegate = delegate;
        this.parseResultsByToken = new WeakMap();
        this.valuesByTokenByElement = new WeakMap();
    }
    get started() {
        return this.tokenListObserver.started;
    }
    start() {
        this.tokenListObserver.start();
    }
    stop() {
        this.tokenListObserver.stop();
    }
    refresh() {
        this.tokenListObserver.refresh();
    }
    get element() {
        return this.tokenListObserver.element;
    }
    get attributeName() {
        return this.tokenListObserver.attributeName;
    }
    tokenMatched(token) {
        const { element: element } = token;
        const { value: value } = this.fetchParseResultForToken(token);
        if (value) {
            this.fetchValuesByTokenForElement(element).set(token, value);
            this.delegate.elementMatchedValue(element, value);
        }
    }
    tokenUnmatched(token) {
        const { element: element } = token;
        const { value: value } = this.fetchParseResultForToken(token);
        if (value) {
            this.fetchValuesByTokenForElement(element).delete(token);
            this.delegate.elementUnmatchedValue(element, value);
        }
    }
    fetchParseResultForToken(token) {
        let parseResult = this.parseResultsByToken.get(token);
        if (!parseResult) {
            parseResult = this.parseToken(token);
            this.parseResultsByToken.set(token, parseResult);
        }
        return parseResult;
    }
    fetchValuesByTokenForElement(element) {
        let valuesByToken = this.valuesByTokenByElement.get(element);
        if (!valuesByToken) {
            valuesByToken = new Map();
            this.valuesByTokenByElement.set(element, valuesByToken);
        }
        return valuesByToken;
    }
    parseToken(token) {
        try {
            const value = this.delegate.parseValueForToken(token);
            return {
                value: value
            };
        } catch (error) {
            return {
                error: error
            };
        }
    }
}
class $ffdf60ebf0144301$var$BindingObserver {
    constructor(context, delegate){
        this.context = context;
        this.delegate = delegate;
        this.bindingsByAction = new Map();
    }
    start() {
        if (!this.valueListObserver) {
            this.valueListObserver = new $ffdf60ebf0144301$export$d27fa4c7330a452e(this.element, this.actionAttribute, this);
            this.valueListObserver.start();
        }
    }
    stop() {
        if (this.valueListObserver) {
            this.valueListObserver.stop();
            delete this.valueListObserver;
            this.disconnectAllActions();
        }
    }
    get element() {
        return this.context.element;
    }
    get identifier() {
        return this.context.identifier;
    }
    get actionAttribute() {
        return this.schema.actionAttribute;
    }
    get schema() {
        return this.context.schema;
    }
    get bindings() {
        return Array.from(this.bindingsByAction.values());
    }
    connectAction(action) {
        const binding = new $ffdf60ebf0144301$var$Binding(this.context, action);
        this.bindingsByAction.set(action, binding);
        this.delegate.bindingConnected(binding);
    }
    disconnectAction(action) {
        const binding = this.bindingsByAction.get(action);
        if (binding) {
            this.bindingsByAction.delete(action);
            this.delegate.bindingDisconnected(binding);
        }
    }
    disconnectAllActions() {
        this.bindings.forEach((binding)=>this.delegate.bindingDisconnected(binding, true));
        this.bindingsByAction.clear();
    }
    parseValueForToken(token) {
        const action = $ffdf60ebf0144301$var$Action.forToken(token, this.schema);
        if (action.identifier == this.identifier) return action;
    }
    elementMatchedValue(element, action) {
        this.connectAction(action);
    }
    elementUnmatchedValue(element, action) {
        this.disconnectAction(action);
    }
}
class $ffdf60ebf0144301$var$ValueObserver {
    constructor(context, receiver){
        this.context = context;
        this.receiver = receiver;
        this.stringMapObserver = new $ffdf60ebf0144301$export$da226bebbf7f6caa(this.element, this);
        this.valueDescriptorMap = this.controller.valueDescriptorMap;
    }
    start() {
        this.stringMapObserver.start();
        this.invokeChangedCallbacksForDefaultValues();
    }
    stop() {
        this.stringMapObserver.stop();
    }
    get element() {
        return this.context.element;
    }
    get controller() {
        return this.context.controller;
    }
    getStringMapKeyForAttribute(attributeName) {
        if (attributeName in this.valueDescriptorMap) return this.valueDescriptorMap[attributeName].name;
    }
    stringMapKeyAdded(key, attributeName) {
        const descriptor = this.valueDescriptorMap[attributeName];
        if (!this.hasValue(key)) this.invokeChangedCallback(key, descriptor.writer(this.receiver[key]), descriptor.writer(descriptor.defaultValue));
    }
    stringMapValueChanged(value, name, oldValue) {
        const descriptor = this.valueDescriptorNameMap[name];
        if (value === null) return;
        if (oldValue === null) oldValue = descriptor.writer(descriptor.defaultValue);
        this.invokeChangedCallback(name, value, oldValue);
    }
    stringMapKeyRemoved(key, attributeName, oldValue) {
        const descriptor = this.valueDescriptorNameMap[key];
        if (this.hasValue(key)) this.invokeChangedCallback(key, descriptor.writer(this.receiver[key]), oldValue);
        else this.invokeChangedCallback(key, descriptor.writer(descriptor.defaultValue), oldValue);
    }
    invokeChangedCallbacksForDefaultValues() {
        for (const { key: key, name: name, defaultValue: defaultValue, writer: writer } of this.valueDescriptors)if (defaultValue != undefined && !this.controller.data.has(key)) this.invokeChangedCallback(name, writer(defaultValue), undefined);
    }
    invokeChangedCallback(name, rawValue, rawOldValue) {
        const changedMethodName = `${name}Changed`;
        const changedMethod = this.receiver[changedMethodName];
        if (typeof changedMethod == "function") {
            const descriptor = this.valueDescriptorNameMap[name];
            try {
                const value = descriptor.reader(rawValue);
                let oldValue = rawOldValue;
                if (rawOldValue) oldValue = descriptor.reader(rawOldValue);
                changedMethod.call(this.receiver, value, oldValue);
            } catch (error) {
                if (error instanceof TypeError) error.message = `Stimulus Value "${this.context.identifier}.${descriptor.name}" - ${error.message}`;
                throw error;
            }
        }
    }
    get valueDescriptors() {
        const { valueDescriptorMap: valueDescriptorMap } = this;
        return Object.keys(valueDescriptorMap).map((key)=>valueDescriptorMap[key]);
    }
    get valueDescriptorNameMap() {
        const descriptors = {};
        Object.keys(this.valueDescriptorMap).forEach((key)=>{
            const descriptor = this.valueDescriptorMap[key];
            descriptors[descriptor.name] = descriptor;
        });
        return descriptors;
    }
    hasValue(attributeName) {
        const descriptor = this.valueDescriptorNameMap[attributeName];
        const hasMethodName = `has${$ffdf60ebf0144301$var$capitalize(descriptor.name)}`;
        return this.receiver[hasMethodName];
    }
}
class $ffdf60ebf0144301$var$TargetObserver {
    constructor(context, delegate){
        this.context = context;
        this.delegate = delegate;
        this.targetsByName = new $ffdf60ebf0144301$export$8bd4ad37b76db6ba();
    }
    start() {
        if (!this.tokenListObserver) {
            this.tokenListObserver = new $ffdf60ebf0144301$export$3aa21c2f03ef6b3b(this.element, this.attributeName, this);
            this.tokenListObserver.start();
        }
    }
    stop() {
        if (this.tokenListObserver) {
            this.disconnectAllTargets();
            this.tokenListObserver.stop();
            delete this.tokenListObserver;
        }
    }
    tokenMatched({ element: element, content: name }) {
        if (this.scope.containsElement(element)) this.connectTarget(element, name);
    }
    tokenUnmatched({ element: element, content: name }) {
        this.disconnectTarget(element, name);
    }
    connectTarget(element, name) {
        var _a;
        if (!this.targetsByName.has(name, element)) {
            this.targetsByName.add(name, element);
            (_a = this.tokenListObserver) === null || _a === void 0 || _a.pause(()=>this.delegate.targetConnected(element, name));
        }
    }
    disconnectTarget(element, name) {
        var _a;
        if (this.targetsByName.has(name, element)) {
            this.targetsByName.delete(name, element);
            (_a = this.tokenListObserver) === null || _a === void 0 || _a.pause(()=>this.delegate.targetDisconnected(element, name));
        }
    }
    disconnectAllTargets() {
        for (const name of this.targetsByName.keys)for (const element of this.targetsByName.getValuesForKey(name))this.disconnectTarget(element, name);
    }
    get attributeName() {
        return `data-${this.context.identifier}-target`;
    }
    get element() {
        return this.context.element;
    }
    get scope() {
        return this.context.scope;
    }
}
function $ffdf60ebf0144301$var$readInheritableStaticArrayValues(constructor, propertyName) {
    const ancestors = $ffdf60ebf0144301$var$getAncestorsForConstructor(constructor);
    return Array.from(ancestors.reduce((values, constructor)=>{
        $ffdf60ebf0144301$var$getOwnStaticArrayValues(constructor, propertyName).forEach((name)=>values.add(name));
        return values;
    }, new Set()));
}
function $ffdf60ebf0144301$var$readInheritableStaticObjectPairs(constructor, propertyName) {
    const ancestors = $ffdf60ebf0144301$var$getAncestorsForConstructor(constructor);
    return ancestors.reduce((pairs, constructor)=>{
        pairs.push(...$ffdf60ebf0144301$var$getOwnStaticObjectPairs(constructor, propertyName));
        return pairs;
    }, []);
}
function $ffdf60ebf0144301$var$getAncestorsForConstructor(constructor) {
    const ancestors = [];
    while(constructor){
        ancestors.push(constructor);
        constructor = Object.getPrototypeOf(constructor);
    }
    return ancestors.reverse();
}
function $ffdf60ebf0144301$var$getOwnStaticArrayValues(constructor, propertyName) {
    const definition = constructor[propertyName];
    return Array.isArray(definition) ? definition : [];
}
function $ffdf60ebf0144301$var$getOwnStaticObjectPairs(constructor, propertyName) {
    const definition = constructor[propertyName];
    return definition ? Object.keys(definition).map((key)=>[
            key,
            definition[key]
        ]) : [];
}
class $ffdf60ebf0144301$var$OutletObserver {
    constructor(context, delegate){
        this.started = false;
        this.context = context;
        this.delegate = delegate;
        this.outletsByName = new $ffdf60ebf0144301$export$8bd4ad37b76db6ba();
        this.outletElementsByName = new $ffdf60ebf0144301$export$8bd4ad37b76db6ba();
        this.selectorObserverMap = new Map();
        this.attributeObserverMap = new Map();
    }
    start() {
        if (!this.started) {
            this.outletDefinitions.forEach((outletName)=>{
                this.setupSelectorObserverForOutlet(outletName);
                this.setupAttributeObserverForOutlet(outletName);
            });
            this.started = true;
            this.dependentContexts.forEach((context)=>context.refresh());
        }
    }
    refresh() {
        this.selectorObserverMap.forEach((observer)=>observer.refresh());
        this.attributeObserverMap.forEach((observer)=>observer.refresh());
    }
    stop() {
        if (this.started) {
            this.started = false;
            this.disconnectAllOutlets();
            this.stopSelectorObservers();
            this.stopAttributeObservers();
        }
    }
    stopSelectorObservers() {
        if (this.selectorObserverMap.size > 0) {
            this.selectorObserverMap.forEach((observer)=>observer.stop());
            this.selectorObserverMap.clear();
        }
    }
    stopAttributeObservers() {
        if (this.attributeObserverMap.size > 0) {
            this.attributeObserverMap.forEach((observer)=>observer.stop());
            this.attributeObserverMap.clear();
        }
    }
    selectorMatched(element, _selector, { outletName: outletName }) {
        const outlet = this.getOutlet(element, outletName);
        if (outlet) this.connectOutlet(outlet, element, outletName);
    }
    selectorUnmatched(element, _selector, { outletName: outletName }) {
        const outlet = this.getOutletFromMap(element, outletName);
        if (outlet) this.disconnectOutlet(outlet, element, outletName);
    }
    selectorMatchElement(element, { outletName: outletName }) {
        const selector = this.selector(outletName);
        const hasOutlet = this.hasOutlet(element, outletName);
        const hasOutletController = element.matches(`[${this.schema.controllerAttribute}~=${outletName}]`);
        if (selector) return hasOutlet && hasOutletController && element.matches(selector);
        else return false;
    }
    elementMatchedAttribute(_element, attributeName) {
        const outletName = this.getOutletNameFromOutletAttributeName(attributeName);
        if (outletName) this.updateSelectorObserverForOutlet(outletName);
    }
    elementAttributeValueChanged(_element, attributeName) {
        const outletName = this.getOutletNameFromOutletAttributeName(attributeName);
        if (outletName) this.updateSelectorObserverForOutlet(outletName);
    }
    elementUnmatchedAttribute(_element, attributeName) {
        const outletName = this.getOutletNameFromOutletAttributeName(attributeName);
        if (outletName) this.updateSelectorObserverForOutlet(outletName);
    }
    connectOutlet(outlet, element, outletName) {
        var _a;
        if (!this.outletElementsByName.has(outletName, element)) {
            this.outletsByName.add(outletName, outlet);
            this.outletElementsByName.add(outletName, element);
            (_a = this.selectorObserverMap.get(outletName)) === null || _a === void 0 || _a.pause(()=>this.delegate.outletConnected(outlet, element, outletName));
        }
    }
    disconnectOutlet(outlet, element, outletName) {
        var _a;
        if (this.outletElementsByName.has(outletName, element)) {
            this.outletsByName.delete(outletName, outlet);
            this.outletElementsByName.delete(outletName, element);
            (_a = this.selectorObserverMap.get(outletName)) === null || _a === void 0 || _a.pause(()=>this.delegate.outletDisconnected(outlet, element, outletName));
        }
    }
    disconnectAllOutlets() {
        for (const outletName of this.outletElementsByName.keys){
            for (const element of this.outletElementsByName.getValuesForKey(outletName))for (const outlet of this.outletsByName.getValuesForKey(outletName))this.disconnectOutlet(outlet, element, outletName);
        }
    }
    updateSelectorObserverForOutlet(outletName) {
        const observer = this.selectorObserverMap.get(outletName);
        if (observer) observer.selector = this.selector(outletName);
    }
    setupSelectorObserverForOutlet(outletName) {
        const selector = this.selector(outletName);
        const selectorObserver = new $ffdf60ebf0144301$export$116e0b48509995b(document.body, selector, this, {
            outletName: outletName
        });
        this.selectorObserverMap.set(outletName, selectorObserver);
        selectorObserver.start();
    }
    setupAttributeObserverForOutlet(outletName) {
        const attributeName = this.attributeNameForOutletName(outletName);
        const attributeObserver = new $ffdf60ebf0144301$export$fe68319d413d05cb(this.scope.element, attributeName, this);
        this.attributeObserverMap.set(outletName, attributeObserver);
        attributeObserver.start();
    }
    selector(outletName) {
        return this.scope.outlets.getSelectorForOutletName(outletName);
    }
    attributeNameForOutletName(outletName) {
        return this.scope.schema.outletAttributeForScope(this.identifier, outletName);
    }
    getOutletNameFromOutletAttributeName(attributeName) {
        return this.outletDefinitions.find((outletName)=>this.attributeNameForOutletName(outletName) === attributeName);
    }
    get outletDependencies() {
        const dependencies = new $ffdf60ebf0144301$export$8bd4ad37b76db6ba();
        this.router.modules.forEach((module)=>{
            const constructor = module.definition.controllerConstructor;
            const outlets = $ffdf60ebf0144301$var$readInheritableStaticArrayValues(constructor, "outlets");
            outlets.forEach((outlet)=>dependencies.add(outlet, module.identifier));
        });
        return dependencies;
    }
    get outletDefinitions() {
        return this.outletDependencies.getKeysForValue(this.identifier);
    }
    get dependentControllerIdentifiers() {
        return this.outletDependencies.getValuesForKey(this.identifier);
    }
    get dependentContexts() {
        const identifiers = this.dependentControllerIdentifiers;
        return this.router.contexts.filter((context)=>identifiers.includes(context.identifier));
    }
    hasOutlet(element, outletName) {
        return !!this.getOutlet(element, outletName) || !!this.getOutletFromMap(element, outletName);
    }
    getOutlet(element, outletName) {
        return this.application.getControllerForElementAndIdentifier(element, outletName);
    }
    getOutletFromMap(element, outletName) {
        return this.outletsByName.getValuesForKey(outletName).find((outlet)=>outlet.element === element);
    }
    get scope() {
        return this.context.scope;
    }
    get schema() {
        return this.context.schema;
    }
    get identifier() {
        return this.context.identifier;
    }
    get application() {
        return this.context.application;
    }
    get router() {
        return this.application.router;
    }
}
class $ffdf60ebf0144301$export$841858b892ce1f4c {
    constructor(module, scope){
        this.logDebugActivity = (functionName, detail = {})=>{
            const { identifier: identifier, controller: controller, element: element } = this;
            detail = Object.assign({
                identifier: identifier,
                controller: controller,
                element: element
            }, detail);
            this.application.logDebugActivity(this.identifier, functionName, detail);
        };
        this.module = module;
        this.scope = scope;
        this.controller = new module.controllerConstructor(this);
        this.bindingObserver = new $ffdf60ebf0144301$var$BindingObserver(this, this.dispatcher);
        this.valueObserver = new $ffdf60ebf0144301$var$ValueObserver(this, this.controller);
        this.targetObserver = new $ffdf60ebf0144301$var$TargetObserver(this, this);
        this.outletObserver = new $ffdf60ebf0144301$var$OutletObserver(this, this);
        try {
            this.controller.initialize();
            this.logDebugActivity("initialize");
        } catch (error) {
            this.handleError(error, "initializing controller");
        }
    }
    connect() {
        this.bindingObserver.start();
        this.valueObserver.start();
        this.targetObserver.start();
        this.outletObserver.start();
        try {
            this.controller.connect();
            this.logDebugActivity("connect");
        } catch (error) {
            this.handleError(error, "connecting controller");
        }
    }
    refresh() {
        this.outletObserver.refresh();
    }
    disconnect() {
        try {
            this.controller.disconnect();
            this.logDebugActivity("disconnect");
        } catch (error) {
            this.handleError(error, "disconnecting controller");
        }
        this.outletObserver.stop();
        this.targetObserver.stop();
        this.valueObserver.stop();
        this.bindingObserver.stop();
    }
    get application() {
        return this.module.application;
    }
    get identifier() {
        return this.module.identifier;
    }
    get schema() {
        return this.application.schema;
    }
    get dispatcher() {
        return this.application.dispatcher;
    }
    get element() {
        return this.scope.element;
    }
    get parentElement() {
        return this.element.parentElement;
    }
    handleError(error, message, detail = {}) {
        const { identifier: identifier, controller: controller, element: element } = this;
        detail = Object.assign({
            identifier: identifier,
            controller: controller,
            element: element
        }, detail);
        this.application.handleError(error, `Error ${message}`, detail);
    }
    targetConnected(element, name) {
        this.invokeControllerMethod(`${name}TargetConnected`, element);
    }
    targetDisconnected(element, name) {
        this.invokeControllerMethod(`${name}TargetDisconnected`, element);
    }
    outletConnected(outlet, element, name) {
        this.invokeControllerMethod(`${$ffdf60ebf0144301$var$namespaceCamelize(name)}OutletConnected`, outlet, element);
    }
    outletDisconnected(outlet, element, name) {
        this.invokeControllerMethod(`${$ffdf60ebf0144301$var$namespaceCamelize(name)}OutletDisconnected`, outlet, element);
    }
    invokeControllerMethod(methodName, ...args) {
        const controller = this.controller;
        if (typeof controller[methodName] == "function") controller[methodName](...args);
    }
}
function $ffdf60ebf0144301$var$bless(constructor) {
    return $ffdf60ebf0144301$var$shadow(constructor, $ffdf60ebf0144301$var$getBlessedProperties(constructor));
}
function $ffdf60ebf0144301$var$shadow(constructor, properties) {
    const shadowConstructor = $ffdf60ebf0144301$var$extend(constructor);
    const shadowProperties = $ffdf60ebf0144301$var$getShadowProperties(constructor.prototype, properties);
    Object.defineProperties(shadowConstructor.prototype, shadowProperties);
    return shadowConstructor;
}
function $ffdf60ebf0144301$var$getBlessedProperties(constructor) {
    const blessings = $ffdf60ebf0144301$var$readInheritableStaticArrayValues(constructor, "blessings");
    return blessings.reduce((blessedProperties, blessing)=>{
        const properties = blessing(constructor);
        for(const key in properties){
            const descriptor = blessedProperties[key] || {};
            blessedProperties[key] = Object.assign(descriptor, properties[key]);
        }
        return blessedProperties;
    }, {});
}
function $ffdf60ebf0144301$var$getShadowProperties(prototype, properties) {
    return $ffdf60ebf0144301$var$getOwnKeys(properties).reduce((shadowProperties, key)=>{
        const descriptor = $ffdf60ebf0144301$var$getShadowedDescriptor(prototype, properties, key);
        if (descriptor) Object.assign(shadowProperties, {
            [key]: descriptor
        });
        return shadowProperties;
    }, {});
}
function $ffdf60ebf0144301$var$getShadowedDescriptor(prototype, properties, key) {
    const shadowingDescriptor = Object.getOwnPropertyDescriptor(prototype, key);
    const shadowedByValue = shadowingDescriptor && "value" in shadowingDescriptor;
    if (!shadowedByValue) {
        const descriptor = Object.getOwnPropertyDescriptor(properties, key).value;
        if (shadowingDescriptor) {
            descriptor.get = shadowingDescriptor.get || descriptor.get;
            descriptor.set = shadowingDescriptor.set || descriptor.set;
        }
        return descriptor;
    }
}
const $ffdf60ebf0144301$var$getOwnKeys = (()=>{
    if (typeof Object.getOwnPropertySymbols == "function") return (object)=>[
            ...Object.getOwnPropertyNames(object),
            ...Object.getOwnPropertySymbols(object)
        ];
    else return Object.getOwnPropertyNames;
})();
const $ffdf60ebf0144301$var$extend = (()=>{
    function extendWithReflect(constructor) {
        function extended() {
            return Reflect.construct(constructor, arguments, new.target);
        }
        extended.prototype = Object.create(constructor.prototype, {
            constructor: {
                value: extended
            }
        });
        Reflect.setPrototypeOf(extended, constructor);
        return extended;
    }
    function testReflectExtension() {
        const a = function() {
            this.a.call(this);
        };
        const b = extendWithReflect(a);
        b.prototype.a = function() {};
        return new b();
    }
    try {
        testReflectExtension();
        return extendWithReflect;
    } catch (error) {
        return (constructor)=>class extended extends constructor {
            };
    }
})();
function $ffdf60ebf0144301$var$blessDefinition(definition) {
    return {
        identifier: definition.identifier,
        controllerConstructor: $ffdf60ebf0144301$var$bless(definition.controllerConstructor)
    };
}
class $ffdf60ebf0144301$var$Module {
    constructor(application, definition){
        this.application = application;
        this.definition = $ffdf60ebf0144301$var$blessDefinition(definition);
        this.contextsByScope = new WeakMap();
        this.connectedContexts = new Set();
    }
    get identifier() {
        return this.definition.identifier;
    }
    get controllerConstructor() {
        return this.definition.controllerConstructor;
    }
    get contexts() {
        return Array.from(this.connectedContexts);
    }
    connectContextForScope(scope) {
        const context = this.fetchContextForScope(scope);
        this.connectedContexts.add(context);
        context.connect();
    }
    disconnectContextForScope(scope) {
        const context = this.contextsByScope.get(scope);
        if (context) {
            this.connectedContexts.delete(context);
            context.disconnect();
        }
    }
    fetchContextForScope(scope) {
        let context = this.contextsByScope.get(scope);
        if (!context) {
            context = new $ffdf60ebf0144301$export$841858b892ce1f4c(this, scope);
            this.contextsByScope.set(scope, context);
        }
        return context;
    }
}
class $ffdf60ebf0144301$var$ClassMap {
    constructor(scope){
        this.scope = scope;
    }
    has(name) {
        return this.data.has(this.getDataKey(name));
    }
    get(name) {
        return this.getAll(name)[0];
    }
    getAll(name) {
        const tokenString = this.data.get(this.getDataKey(name)) || "";
        return $ffdf60ebf0144301$var$tokenize(tokenString);
    }
    getAttributeName(name) {
        return this.data.getAttributeNameForKey(this.getDataKey(name));
    }
    getDataKey(name) {
        return `${name}-class`;
    }
    get data() {
        return this.scope.data;
    }
}
class $ffdf60ebf0144301$var$DataMap {
    constructor(scope){
        this.scope = scope;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get(key) {
        const name = this.getAttributeNameForKey(key);
        return this.element.getAttribute(name);
    }
    set(key, value) {
        const name = this.getAttributeNameForKey(key);
        this.element.setAttribute(name, value);
        return this.get(key);
    }
    has(key) {
        const name = this.getAttributeNameForKey(key);
        return this.element.hasAttribute(name);
    }
    delete(key) {
        if (this.has(key)) {
            const name = this.getAttributeNameForKey(key);
            this.element.removeAttribute(name);
            return true;
        } else return false;
    }
    getAttributeNameForKey(key) {
        return `data-${this.identifier}-${$ffdf60ebf0144301$var$dasherize(key)}`;
    }
}
class $ffdf60ebf0144301$var$Guide {
    constructor(logger){
        this.warnedKeysByObject = new WeakMap();
        this.logger = logger;
    }
    warn(object, key, message) {
        let warnedKeys = this.warnedKeysByObject.get(object);
        if (!warnedKeys) {
            warnedKeys = new Set();
            this.warnedKeysByObject.set(object, warnedKeys);
        }
        if (!warnedKeys.has(key)) {
            warnedKeys.add(key);
            this.logger.warn(message, object);
        }
    }
}
function $ffdf60ebf0144301$var$attributeValueContainsToken(attributeName, token) {
    return `[${attributeName}~="${token}"]`;
}
class $ffdf60ebf0144301$var$TargetSet {
    constructor(scope){
        this.scope = scope;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get schema() {
        return this.scope.schema;
    }
    has(targetName) {
        return this.find(targetName) != null;
    }
    find(...targetNames) {
        return targetNames.reduce((target, targetName)=>target || this.findTarget(targetName) || this.findLegacyTarget(targetName), undefined);
    }
    findAll(...targetNames) {
        return targetNames.reduce((targets, targetName)=>[
                ...targets,
                ...this.findAllTargets(targetName),
                ...this.findAllLegacyTargets(targetName)
            ], []);
    }
    findTarget(targetName) {
        const selector = this.getSelectorForTargetName(targetName);
        return this.scope.findElement(selector);
    }
    findAllTargets(targetName) {
        const selector = this.getSelectorForTargetName(targetName);
        return this.scope.findAllElements(selector);
    }
    getSelectorForTargetName(targetName) {
        const attributeName = this.schema.targetAttributeForScope(this.identifier);
        return $ffdf60ebf0144301$var$attributeValueContainsToken(attributeName, targetName);
    }
    findLegacyTarget(targetName) {
        const selector = this.getLegacySelectorForTargetName(targetName);
        return this.deprecate(this.scope.findElement(selector), targetName);
    }
    findAllLegacyTargets(targetName) {
        const selector = this.getLegacySelectorForTargetName(targetName);
        return this.scope.findAllElements(selector).map((element)=>this.deprecate(element, targetName));
    }
    getLegacySelectorForTargetName(targetName) {
        const targetDescriptor = `${this.identifier}.${targetName}`;
        return $ffdf60ebf0144301$var$attributeValueContainsToken(this.schema.targetAttribute, targetDescriptor);
    }
    deprecate(element, targetName) {
        if (element) {
            const { identifier: identifier } = this;
            const attributeName = this.schema.targetAttribute;
            const revisedAttributeName = this.schema.targetAttributeForScope(identifier);
            this.guide.warn(element, `target:${targetName}`, `Please replace ${attributeName}="${identifier}.${targetName}" with ${revisedAttributeName}="${targetName}". ` + `The ${attributeName} attribute is deprecated and will be removed in a future version of Stimulus.`);
        }
        return element;
    }
    get guide() {
        return this.scope.guide;
    }
}
class $ffdf60ebf0144301$var$OutletSet {
    constructor(scope, controllerElement){
        this.scope = scope;
        this.controllerElement = controllerElement;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get schema() {
        return this.scope.schema;
    }
    has(outletName) {
        return this.find(outletName) != null;
    }
    find(...outletNames) {
        return outletNames.reduce((outlet, outletName)=>outlet || this.findOutlet(outletName), undefined);
    }
    findAll(...outletNames) {
        return outletNames.reduce((outlets, outletName)=>[
                ...outlets,
                ...this.findAllOutlets(outletName)
            ], []);
    }
    getSelectorForOutletName(outletName) {
        const attributeName = this.schema.outletAttributeForScope(this.identifier, outletName);
        return this.controllerElement.getAttribute(attributeName);
    }
    findOutlet(outletName) {
        const selector = this.getSelectorForOutletName(outletName);
        if (selector) return this.findElement(selector, outletName);
    }
    findAllOutlets(outletName) {
        const selector = this.getSelectorForOutletName(outletName);
        return selector ? this.findAllElements(selector, outletName) : [];
    }
    findElement(selector, outletName) {
        const elements = this.scope.queryElements(selector);
        return elements.filter((element)=>this.matchesElement(element, selector, outletName))[0];
    }
    findAllElements(selector, outletName) {
        const elements = this.scope.queryElements(selector);
        return elements.filter((element)=>this.matchesElement(element, selector, outletName));
    }
    matchesElement(element, selector, outletName) {
        const controllerAttribute = element.getAttribute(this.scope.schema.controllerAttribute) || "";
        return element.matches(selector) && controllerAttribute.split(" ").includes(outletName);
    }
}
class $ffdf60ebf0144301$var$Scope {
    constructor(schema, element, identifier, logger){
        this.targets = new $ffdf60ebf0144301$var$TargetSet(this);
        this.classes = new $ffdf60ebf0144301$var$ClassMap(this);
        this.data = new $ffdf60ebf0144301$var$DataMap(this);
        this.containsElement = (element)=>{
            return element.closest(this.controllerSelector) === this.element;
        };
        this.schema = schema;
        this.element = element;
        this.identifier = identifier;
        this.guide = new $ffdf60ebf0144301$var$Guide(logger);
        this.outlets = new $ffdf60ebf0144301$var$OutletSet(this.documentScope, element);
    }
    findElement(selector) {
        return this.element.matches(selector) ? this.element : this.queryElements(selector).find(this.containsElement);
    }
    findAllElements(selector) {
        return [
            ...this.element.matches(selector) ? [
                this.element
            ] : [],
            ...this.queryElements(selector).filter(this.containsElement)
        ];
    }
    queryElements(selector) {
        return Array.from(this.element.querySelectorAll(selector));
    }
    get controllerSelector() {
        return $ffdf60ebf0144301$var$attributeValueContainsToken(this.schema.controllerAttribute, this.identifier);
    }
    get isDocumentScope() {
        return this.element === document.documentElement;
    }
    get documentScope() {
        return this.isDocumentScope ? this : new $ffdf60ebf0144301$var$Scope(this.schema, document.documentElement, this.identifier, this.guide.logger);
    }
}
class $ffdf60ebf0144301$var$ScopeObserver {
    constructor(element, schema, delegate){
        this.element = element;
        this.schema = schema;
        this.delegate = delegate;
        this.valueListObserver = new $ffdf60ebf0144301$export$d27fa4c7330a452e(this.element, this.controllerAttribute, this);
        this.scopesByIdentifierByElement = new WeakMap();
        this.scopeReferenceCounts = new WeakMap();
    }
    start() {
        this.valueListObserver.start();
    }
    stop() {
        this.valueListObserver.stop();
    }
    get controllerAttribute() {
        return this.schema.controllerAttribute;
    }
    parseValueForToken(token) {
        const { element: element, content: identifier } = token;
        return this.parseValueForElementAndIdentifier(element, identifier);
    }
    parseValueForElementAndIdentifier(element, identifier) {
        const scopesByIdentifier = this.fetchScopesByIdentifierForElement(element);
        let scope = scopesByIdentifier.get(identifier);
        if (!scope) {
            scope = this.delegate.createScopeForElementAndIdentifier(element, identifier);
            scopesByIdentifier.set(identifier, scope);
        }
        return scope;
    }
    elementMatchedValue(element, value) {
        const referenceCount = (this.scopeReferenceCounts.get(value) || 0) + 1;
        this.scopeReferenceCounts.set(value, referenceCount);
        if (referenceCount == 1) this.delegate.scopeConnected(value);
    }
    elementUnmatchedValue(element, value) {
        const referenceCount = this.scopeReferenceCounts.get(value);
        if (referenceCount) {
            this.scopeReferenceCounts.set(value, referenceCount - 1);
            if (referenceCount == 1) this.delegate.scopeDisconnected(value);
        }
    }
    fetchScopesByIdentifierForElement(element) {
        let scopesByIdentifier = this.scopesByIdentifierByElement.get(element);
        if (!scopesByIdentifier) {
            scopesByIdentifier = new Map();
            this.scopesByIdentifierByElement.set(element, scopesByIdentifier);
        }
        return scopesByIdentifier;
    }
}
class $ffdf60ebf0144301$var$Router {
    constructor(application){
        this.application = application;
        this.scopeObserver = new $ffdf60ebf0144301$var$ScopeObserver(this.element, this.schema, this);
        this.scopesByIdentifier = new $ffdf60ebf0144301$export$8bd4ad37b76db6ba();
        this.modulesByIdentifier = new Map();
    }
    get element() {
        return this.application.element;
    }
    get schema() {
        return this.application.schema;
    }
    get logger() {
        return this.application.logger;
    }
    get controllerAttribute() {
        return this.schema.controllerAttribute;
    }
    get modules() {
        return Array.from(this.modulesByIdentifier.values());
    }
    get contexts() {
        return this.modules.reduce((contexts, module)=>contexts.concat(module.contexts), []);
    }
    start() {
        this.scopeObserver.start();
    }
    stop() {
        this.scopeObserver.stop();
    }
    loadDefinition(definition) {
        this.unloadIdentifier(definition.identifier);
        const module = new $ffdf60ebf0144301$var$Module(this.application, definition);
        this.connectModule(module);
        const afterLoad = definition.controllerConstructor.afterLoad;
        if (afterLoad) afterLoad.call(definition.controllerConstructor, definition.identifier, this.application);
    }
    unloadIdentifier(identifier) {
        const module = this.modulesByIdentifier.get(identifier);
        if (module) this.disconnectModule(module);
    }
    getContextForElementAndIdentifier(element, identifier) {
        const module = this.modulesByIdentifier.get(identifier);
        if (module) return module.contexts.find((context)=>context.element == element);
    }
    proposeToConnectScopeForElementAndIdentifier(element, identifier) {
        const scope = this.scopeObserver.parseValueForElementAndIdentifier(element, identifier);
        if (scope) this.scopeObserver.elementMatchedValue(scope.element, scope);
        else console.error(`Couldn't find or create scope for identifier: "${identifier}" and element:`, element);
    }
    handleError(error, message, detail) {
        this.application.handleError(error, message, detail);
    }
    createScopeForElementAndIdentifier(element, identifier) {
        return new $ffdf60ebf0144301$var$Scope(this.schema, element, identifier, this.logger);
    }
    scopeConnected(scope) {
        this.scopesByIdentifier.add(scope.identifier, scope);
        const module = this.modulesByIdentifier.get(scope.identifier);
        if (module) module.connectContextForScope(scope);
    }
    scopeDisconnected(scope) {
        this.scopesByIdentifier.delete(scope.identifier, scope);
        const module = this.modulesByIdentifier.get(scope.identifier);
        if (module) module.disconnectContextForScope(scope);
    }
    connectModule(module) {
        this.modulesByIdentifier.set(module.identifier, module);
        const scopes = this.scopesByIdentifier.getValuesForKey(module.identifier);
        scopes.forEach((scope)=>module.connectContextForScope(scope));
    }
    disconnectModule(module) {
        this.modulesByIdentifier.delete(module.identifier);
        const scopes = this.scopesByIdentifier.getValuesForKey(module.identifier);
        scopes.forEach((scope)=>module.disconnectContextForScope(scope));
    }
}
const $ffdf60ebf0144301$export$1db618b7b2275ea1 = {
    controllerAttribute: "data-controller",
    actionAttribute: "data-action",
    targetAttribute: "data-target",
    targetAttributeForScope: (identifier)=>`data-${identifier}-target`,
    outletAttributeForScope: (identifier, outlet)=>`data-${identifier}-${outlet}-outlet`,
    keyMappings: Object.assign(Object.assign({
        enter: "Enter",
        tab: "Tab",
        esc: "Escape",
        space: " ",
        up: "ArrowUp",
        down: "ArrowDown",
        left: "ArrowLeft",
        right: "ArrowRight",
        home: "Home",
        end: "End",
        page_up: "PageUp",
        page_down: "PageDown"
    }, $ffdf60ebf0144301$var$objectFromEntries("abcdefghijklmnopqrstuvwxyz".split("").map((c)=>[
            c,
            c
        ]))), $ffdf60ebf0144301$var$objectFromEntries("0123456789".split("").map((n)=>[
            n,
            n
        ])))
};
function $ffdf60ebf0144301$var$objectFromEntries(array) {
    return array.reduce((memo, [k, v])=>Object.assign(Object.assign({}, memo), {
            [k]: v
        }), {});
}
class $ffdf60ebf0144301$export$16975c34e60e1e61 {
    constructor(element = document.documentElement, schema = $ffdf60ebf0144301$export$1db618b7b2275ea1){
        this.logger = console;
        this.debug = false;
        this.logDebugActivity = (identifier, functionName, detail = {})=>{
            if (this.debug) this.logFormattedMessage(identifier, functionName, detail);
        };
        this.element = element;
        this.schema = schema;
        this.dispatcher = new $ffdf60ebf0144301$var$Dispatcher(this);
        this.router = new $ffdf60ebf0144301$var$Router(this);
        this.actionDescriptorFilters = Object.assign({}, $ffdf60ebf0144301$var$defaultActionDescriptorFilters);
    }
    static start(element, schema) {
        const application = new this(element, schema);
        application.start();
        return application;
    }
    async start() {
        await $ffdf60ebf0144301$var$domReady();
        this.logDebugActivity("application", "starting");
        this.dispatcher.start();
        this.router.start();
        this.logDebugActivity("application", "start");
    }
    stop() {
        this.logDebugActivity("application", "stopping");
        this.dispatcher.stop();
        this.router.stop();
        this.logDebugActivity("application", "stop");
    }
    register(identifier, controllerConstructor) {
        this.load({
            identifier: identifier,
            controllerConstructor: controllerConstructor
        });
    }
    registerActionOption(name, filter) {
        this.actionDescriptorFilters[name] = filter;
    }
    load(head, ...rest) {
        const definitions = Array.isArray(head) ? head : [
            head,
            ...rest
        ];
        definitions.forEach((definition)=>{
            if (definition.controllerConstructor.shouldLoad) this.router.loadDefinition(definition);
        });
    }
    unload(head, ...rest) {
        const identifiers = Array.isArray(head) ? head : [
            head,
            ...rest
        ];
        identifiers.forEach((identifier)=>this.router.unloadIdentifier(identifier));
    }
    get controllers() {
        return this.router.contexts.map((context)=>context.controller);
    }
    getControllerForElementAndIdentifier(element, identifier) {
        const context = this.router.getContextForElementAndIdentifier(element, identifier);
        return context ? context.controller : null;
    }
    handleError(error, message, detail) {
        var _a;
        this.logger.error(`%s\n\n%o\n\n%o`, message, error, detail);
        (_a = window.onerror) === null || _a === void 0 || _a.call(window, message, "", 0, 0, error);
    }
    logFormattedMessage(identifier, functionName, detail = {}) {
        detail = Object.assign({
            application: this
        }, detail);
        this.logger.groupCollapsed(`${identifier} #${functionName}`);
        this.logger.log("details:", Object.assign({}, detail));
        this.logger.groupEnd();
    }
}
function $ffdf60ebf0144301$var$domReady() {
    return new Promise((resolve)=>{
        if (document.readyState == "loading") document.addEventListener("DOMContentLoaded", ()=>resolve());
        else resolve();
    });
}
function $ffdf60ebf0144301$var$ClassPropertiesBlessing(constructor) {
    const classes = $ffdf60ebf0144301$var$readInheritableStaticArrayValues(constructor, "classes");
    return classes.reduce((properties, classDefinition)=>{
        return Object.assign(properties, $ffdf60ebf0144301$var$propertiesForClassDefinition(classDefinition));
    }, {});
}
function $ffdf60ebf0144301$var$propertiesForClassDefinition(key) {
    return {
        [`${key}Class`]: {
            get () {
                const { classes: classes } = this;
                if (classes.has(key)) return classes.get(key);
                else {
                    const attribute = classes.getAttributeName(key);
                    throw new Error(`Missing attribute "${attribute}"`);
                }
            }
        },
        [`${key}Classes`]: {
            get () {
                return this.classes.getAll(key);
            }
        },
        [`has${$ffdf60ebf0144301$var$capitalize(key)}Class`]: {
            get () {
                return this.classes.has(key);
            }
        }
    };
}
function $ffdf60ebf0144301$var$OutletPropertiesBlessing(constructor) {
    const outlets = $ffdf60ebf0144301$var$readInheritableStaticArrayValues(constructor, "outlets");
    return outlets.reduce((properties, outletDefinition)=>{
        return Object.assign(properties, $ffdf60ebf0144301$var$propertiesForOutletDefinition(outletDefinition));
    }, {});
}
function $ffdf60ebf0144301$var$getOutletController(controller, element, identifier) {
    return controller.application.getControllerForElementAndIdentifier(element, identifier);
}
function $ffdf60ebf0144301$var$getControllerAndEnsureConnectedScope(controller, element, outletName) {
    let outletController = $ffdf60ebf0144301$var$getOutletController(controller, element, outletName);
    if (outletController) return outletController;
    controller.application.router.proposeToConnectScopeForElementAndIdentifier(element, outletName);
    outletController = $ffdf60ebf0144301$var$getOutletController(controller, element, outletName);
    if (outletController) return outletController;
}
function $ffdf60ebf0144301$var$propertiesForOutletDefinition(name) {
    const camelizedName = $ffdf60ebf0144301$var$namespaceCamelize(name);
    return {
        [`${camelizedName}Outlet`]: {
            get () {
                const outletElement = this.outlets.find(name);
                const selector = this.outlets.getSelectorForOutletName(name);
                if (outletElement) {
                    const outletController = $ffdf60ebf0144301$var$getControllerAndEnsureConnectedScope(this, outletElement, name);
                    if (outletController) return outletController;
                    throw new Error(`The provided outlet element is missing an outlet controller "${name}" instance for host controller "${this.identifier}"`);
                }
                throw new Error(`Missing outlet element "${name}" for host controller "${this.identifier}". Stimulus couldn't find a matching outlet element using selector "${selector}".`);
            }
        },
        [`${camelizedName}Outlets`]: {
            get () {
                const outlets = this.outlets.findAll(name);
                if (outlets.length > 0) return outlets.map((outletElement)=>{
                    const outletController = $ffdf60ebf0144301$var$getControllerAndEnsureConnectedScope(this, outletElement, name);
                    if (outletController) return outletController;
                    console.warn(`The provided outlet element is missing an outlet controller "${name}" instance for host controller "${this.identifier}"`, outletElement);
                }).filter((controller)=>controller);
                return [];
            }
        },
        [`${camelizedName}OutletElement`]: {
            get () {
                const outletElement = this.outlets.find(name);
                const selector = this.outlets.getSelectorForOutletName(name);
                if (outletElement) return outletElement;
                else throw new Error(`Missing outlet element "${name}" for host controller "${this.identifier}". Stimulus couldn't find a matching outlet element using selector "${selector}".`);
            }
        },
        [`${camelizedName}OutletElements`]: {
            get () {
                return this.outlets.findAll(name);
            }
        },
        [`has${$ffdf60ebf0144301$var$capitalize(camelizedName)}Outlet`]: {
            get () {
                return this.outlets.has(name);
            }
        }
    };
}
function $ffdf60ebf0144301$var$TargetPropertiesBlessing(constructor) {
    const targets = $ffdf60ebf0144301$var$readInheritableStaticArrayValues(constructor, "targets");
    return targets.reduce((properties, targetDefinition)=>{
        return Object.assign(properties, $ffdf60ebf0144301$var$propertiesForTargetDefinition(targetDefinition));
    }, {});
}
function $ffdf60ebf0144301$var$propertiesForTargetDefinition(name) {
    return {
        [`${name}Target`]: {
            get () {
                const target = this.targets.find(name);
                if (target) return target;
                else throw new Error(`Missing target element "${name}" for "${this.identifier}" controller`);
            }
        },
        [`${name}Targets`]: {
            get () {
                return this.targets.findAll(name);
            }
        },
        [`has${$ffdf60ebf0144301$var$capitalize(name)}Target`]: {
            get () {
                return this.targets.has(name);
            }
        }
    };
}
function $ffdf60ebf0144301$var$ValuePropertiesBlessing(constructor) {
    const valueDefinitionPairs = $ffdf60ebf0144301$var$readInheritableStaticObjectPairs(constructor, "values");
    const propertyDescriptorMap = {
        valueDescriptorMap: {
            get () {
                return valueDefinitionPairs.reduce((result, valueDefinitionPair)=>{
                    const valueDescriptor = $ffdf60ebf0144301$var$parseValueDefinitionPair(valueDefinitionPair, this.identifier);
                    const attributeName = this.data.getAttributeNameForKey(valueDescriptor.key);
                    return Object.assign(result, {
                        [attributeName]: valueDescriptor
                    });
                }, {});
            }
        }
    };
    return valueDefinitionPairs.reduce((properties, valueDefinitionPair)=>{
        return Object.assign(properties, $ffdf60ebf0144301$var$propertiesForValueDefinitionPair(valueDefinitionPair));
    }, propertyDescriptorMap);
}
function $ffdf60ebf0144301$var$propertiesForValueDefinitionPair(valueDefinitionPair, controller) {
    const definition = $ffdf60ebf0144301$var$parseValueDefinitionPair(valueDefinitionPair, controller);
    const { key: key, name: name, reader: read, writer: write } = definition;
    return {
        [name]: {
            get () {
                const value = this.data.get(key);
                if (value !== null) return read(value);
                else return definition.defaultValue;
            },
            set (value) {
                if (value === undefined) this.data.delete(key);
                else this.data.set(key, write(value));
            }
        },
        [`has${$ffdf60ebf0144301$var$capitalize(name)}`]: {
            get () {
                return this.data.has(key) || definition.hasCustomDefaultValue;
            }
        }
    };
}
function $ffdf60ebf0144301$var$parseValueDefinitionPair([token, typeDefinition], controller) {
    return $ffdf60ebf0144301$var$valueDescriptorForTokenAndTypeDefinition({
        controller: controller,
        token: token,
        typeDefinition: typeDefinition
    });
}
function $ffdf60ebf0144301$var$parseValueTypeConstant(constant) {
    switch(constant){
        case Array:
            return "array";
        case Boolean:
            return "boolean";
        case Number:
            return "number";
        case Object:
            return "object";
        case String:
            return "string";
    }
}
function $ffdf60ebf0144301$var$parseValueTypeDefault(defaultValue) {
    switch(typeof defaultValue){
        case "boolean":
            return "boolean";
        case "number":
            return "number";
        case "string":
            return "string";
    }
    if (Array.isArray(defaultValue)) return "array";
    if (Object.prototype.toString.call(defaultValue) === "[object Object]") return "object";
}
function $ffdf60ebf0144301$var$parseValueTypeObject(payload) {
    const { controller: controller, token: token, typeObject: typeObject } = payload;
    const hasType = $ffdf60ebf0144301$var$isSomething(typeObject.type);
    const hasDefault = $ffdf60ebf0144301$var$isSomething(typeObject.default);
    const fullObject = hasType && hasDefault;
    const onlyType = hasType && !hasDefault;
    const onlyDefault = !hasType && hasDefault;
    const typeFromObject = $ffdf60ebf0144301$var$parseValueTypeConstant(typeObject.type);
    const typeFromDefaultValue = $ffdf60ebf0144301$var$parseValueTypeDefault(payload.typeObject.default);
    if (onlyType) return typeFromObject;
    if (onlyDefault) return typeFromDefaultValue;
    if (typeFromObject !== typeFromDefaultValue) {
        const propertyPath = controller ? `${controller}.${token}` : token;
        throw new Error(`The specified default value for the Stimulus Value "${propertyPath}" must match the defined type "${typeFromObject}". The provided default value of "${typeObject.default}" is of type "${typeFromDefaultValue}".`);
    }
    if (fullObject) return typeFromObject;
}
function $ffdf60ebf0144301$var$parseValueTypeDefinition(payload) {
    const { controller: controller, token: token, typeDefinition: typeDefinition } = payload;
    const typeObject = {
        controller: controller,
        token: token,
        typeObject: typeDefinition
    };
    const typeFromObject = $ffdf60ebf0144301$var$parseValueTypeObject(typeObject);
    const typeFromDefaultValue = $ffdf60ebf0144301$var$parseValueTypeDefault(typeDefinition);
    const typeFromConstant = $ffdf60ebf0144301$var$parseValueTypeConstant(typeDefinition);
    const type = typeFromObject || typeFromDefaultValue || typeFromConstant;
    if (type) return type;
    const propertyPath = controller ? `${controller}.${typeDefinition}` : token;
    throw new Error(`Unknown value type "${propertyPath}" for "${token}" value`);
}
function $ffdf60ebf0144301$var$defaultValueForDefinition(typeDefinition) {
    const constant = $ffdf60ebf0144301$var$parseValueTypeConstant(typeDefinition);
    if (constant) return $ffdf60ebf0144301$var$defaultValuesByType[constant];
    const hasDefault = $ffdf60ebf0144301$var$hasProperty(typeDefinition, "default");
    const hasType = $ffdf60ebf0144301$var$hasProperty(typeDefinition, "type");
    const typeObject = typeDefinition;
    if (hasDefault) return typeObject.default;
    if (hasType) {
        const { type: type } = typeObject;
        const constantFromType = $ffdf60ebf0144301$var$parseValueTypeConstant(type);
        if (constantFromType) return $ffdf60ebf0144301$var$defaultValuesByType[constantFromType];
    }
    return typeDefinition;
}
function $ffdf60ebf0144301$var$valueDescriptorForTokenAndTypeDefinition(payload) {
    const { token: token, typeDefinition: typeDefinition } = payload;
    const key = `${$ffdf60ebf0144301$var$dasherize(token)}-value`;
    const type = $ffdf60ebf0144301$var$parseValueTypeDefinition(payload);
    return {
        type: type,
        key: key,
        name: $ffdf60ebf0144301$var$camelize(key),
        get defaultValue () {
            return $ffdf60ebf0144301$var$defaultValueForDefinition(typeDefinition);
        },
        get hasCustomDefaultValue () {
            return $ffdf60ebf0144301$var$parseValueTypeDefault(typeDefinition) !== undefined;
        },
        reader: $ffdf60ebf0144301$var$readers[type],
        writer: $ffdf60ebf0144301$var$writers[type] || $ffdf60ebf0144301$var$writers.default
    };
}
const $ffdf60ebf0144301$var$defaultValuesByType = {
    get array () {
        return [];
    },
    boolean: false,
    number: 0,
    get object () {
        return {};
    },
    string: ""
};
const $ffdf60ebf0144301$var$readers = {
    array (value) {
        const array = JSON.parse(value);
        if (!Array.isArray(array)) throw new TypeError(`expected value of type "array" but instead got value "${value}" of type "${$ffdf60ebf0144301$var$parseValueTypeDefault(array)}"`);
        return array;
    },
    boolean (value) {
        return !(value == "0" || String(value).toLowerCase() == "false");
    },
    number (value) {
        return Number(value.replace(/_/g, ""));
    },
    object (value) {
        const object = JSON.parse(value);
        if (object === null || typeof object != "object" || Array.isArray(object)) throw new TypeError(`expected value of type "object" but instead got value "${value}" of type "${$ffdf60ebf0144301$var$parseValueTypeDefault(object)}"`);
        return object;
    },
    string (value) {
        return value;
    }
};
const $ffdf60ebf0144301$var$writers = {
    default: $ffdf60ebf0144301$var$writeString,
    array: $ffdf60ebf0144301$var$writeJSON,
    object: $ffdf60ebf0144301$var$writeJSON
};
function $ffdf60ebf0144301$var$writeJSON(value) {
    return JSON.stringify(value);
}
function $ffdf60ebf0144301$var$writeString(value) {
    return `${value}`;
}
class $ffdf60ebf0144301$export$bd0bf19f25da8474 {
    constructor(context){
        this.context = context;
    }
    static get shouldLoad() {
        return true;
    }
    static afterLoad(_identifier, _application) {
        return;
    }
    get application() {
        return this.context.application;
    }
    get scope() {
        return this.context.scope;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get targets() {
        return this.scope.targets;
    }
    get outlets() {
        return this.scope.outlets;
    }
    get classes() {
        return this.scope.classes;
    }
    get data() {
        return this.scope.data;
    }
    initialize() {}
    connect() {}
    disconnect() {}
    dispatch(eventName, { target: target = this.element, detail: detail = {}, prefix: prefix = this.identifier, bubbles: bubbles = true, cancelable: cancelable = true } = {}) {
        const type = prefix ? `${prefix}:${eventName}` : eventName;
        const event = new CustomEvent(type, {
            detail: detail,
            bubbles: bubbles,
            cancelable: cancelable
        });
        target.dispatchEvent(event);
        return event;
    }
}
$ffdf60ebf0144301$export$bd0bf19f25da8474.blessings = [
    $ffdf60ebf0144301$var$ClassPropertiesBlessing,
    $ffdf60ebf0144301$var$TargetPropertiesBlessing,
    $ffdf60ebf0144301$var$ValuePropertiesBlessing,
    $ffdf60ebf0144301$var$OutletPropertiesBlessing
];
$ffdf60ebf0144301$export$bd0bf19f25da8474.targets = [];
$ffdf60ebf0144301$export$bd0bf19f25da8474.outlets = [];
$ffdf60ebf0144301$export$bd0bf19f25da8474.values = {};



class $6d9413c31e161bfa$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "trigger"
    ];
    connect() {
        this._storageKey = "docs-sidebar-accordion";
        this._boundHashChange = this._onHashChange.bind(this);
        window.addEventListener("hashchange", this._boundHashChange);
        const persisted = this._readState();
        const hash = this._currentHash();
        const activeLink = this._linkForHash(hash) || this._firstLink();
        this.triggerTargets.forEach((trigger)=>{
            const panel = this._panelFor(trigger);
            if (!panel) return;
            const label = this._labelFor(trigger);
            const containsActive = !!activeLink && panel.contains(activeLink);
            const saved = persisted[label];
            let open;
            if (hash && containsActive) // Deep-link into this group — always reveal it, overriding a saved-closed.
            open = true;
            else if (typeof saved === "boolean") open = saved;
            else open = containsActive;
            this._apply(trigger, panel, open);
        });
    }
    disconnect() {
        window.removeEventListener("hashchange", this._boundHashChange);
    }
    // ── Actions ────────────────────────────────────────────────────────────────
    toggle(event) {
        const trigger = event.currentTarget;
        const panel = this._panelFor(trigger);
        if (!panel) return;
        const open = trigger.getAttribute("aria-expanded") !== "true";
        this._apply(trigger, panel, open);
        this._saveState(this._labelFor(trigger), open);
    }
    // ── Internals ────────────────────────────────────────────────────────────────
    _onHashChange() {
        const activeLink = this._linkForHash(this._currentHash());
        if (!activeLink) return;
        // Walk up and expand every accordion panel that contains the linked item so
        // a deep link into a collapsed group reveals it in the nav.
        let el = activeLink.parentElement;
        while(el && el !== this.element){
            if (el.matches('[data-accordion-target="panel"]') && el.hidden) {
                const trigger = this._triggerFor(el);
                if (trigger) {
                    this._apply(trigger, el, true);
                    this._saveState(this._labelFor(trigger), true);
                }
            }
            el = el.parentElement;
        }
    }
    _apply(trigger, panel, open) {
        trigger.setAttribute("aria-expanded", open ? "true" : "false");
        panel.hidden = !open;
    }
    _panelFor(trigger) {
        const id = trigger.getAttribute("aria-controls");
        return id ? document.getElementById(id) : null;
    }
    _triggerFor(panel) {
        return this.triggerTargets.find((t)=>t.getAttribute("aria-controls") === panel.id);
    }
    _labelFor(trigger) {
        const span = trigger.querySelector(".docs-sidebar-label-text");
        return (span ? span.textContent : trigger.textContent).trim();
    }
    _currentHash() {
        return window.location.hash.slice(1).split("?")[0].replace(/\/$/, "").trim();
    }
    _linkHash(link) {
        const href = link.getAttribute("href") || "";
        const i = href.indexOf("#");
        if (i === -1) return "";
        return href.slice(i + 1).split("?")[0].replace(/\/$/, "").trim();
    }
    _linkForHash(hash) {
        if (!hash) return null;
        const links = this.element.querySelectorAll("a.docs-sidebar-link");
        for (const link of links){
            if (this._linkHash(link) === hash) return link;
        }
        return null;
    }
    _firstLink() {
        return this.element.querySelector("a.docs-sidebar-link");
    }
    _readState() {
        try {
            const raw = window.localStorage.getItem(this._storageKey);
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch (e) {
            return {};
        }
    }
    _saveState(label, open) {
        if (!label) return;
        try {
            const state = this._readState();
            state[label] = open;
            window.localStorage.setItem(this._storageKey, JSON.stringify(state));
        } catch (e) {
        /* localStorage unavailable (private mode / quota) — non-fatal. */ }
    }
}



const $9edb7d9b6ecc0921$var$MAX_HISTORY = 6; // last 3 turns (user + assistant pairs)
const $9edb7d9b6ecc0921$var$SESSION_KEY = "docs-chat-v1";
class $9edb7d9b6ecc0921$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "input",
        "messages",
        "send",
        "toggle",
        "panel",
        "starters",
        "counter",
        "chatWrapper"
    ];
    connect() {
        this._abortController = null;
        this._loading = false;
        this._history = [];
        this._sessionMessages = []; // {role, text}[] for sessionStorage
        this._restoreSession();
        this._updateCounter();
    }
    disconnect() {
        this._abortController?.abort();
    }
    togglePanel() {
        const isLarge = window.innerWidth >= 768;
        const wrapper = this.chatWrapperTarget;
        if (isLarge) {
            const isOpen = wrapper.classList.toggle("chat-side-open");
            this.element.classList.toggle("chat-side-open", isOpen);
            this.toggleTargets.forEach((t)=>t.setAttribute("aria-expanded", String(isOpen)));
            if (isOpen) this.inputTarget.focus();
        } else {
            const panel = this.panelTarget;
            const isOpen = panel.classList.toggle("chat-open");
            this.toggleTargets.forEach((t)=>t.setAttribute("aria-expanded", String(isOpen)));
            if (isOpen) this.inputTarget.focus();
        }
    }
    // ── Starter questions ─────────────────────────────────────────────────────
    sendStarter(event) {
        const question = event.currentTarget.dataset.question;
        if (!question) return;
        this.inputTarget.value = question;
        this._updateCounter();
        this._resizeInput();
        this.send();
    }
    // ── Input events ──────────────────────────────────────────────────────────
    onInput() {
        this._updateCounter();
        this._resizeInput();
    }
    _updateCounter() {
        const len = this.inputTarget.value.length;
        this.counterTarget.textContent = `${len} / 500`;
        this.counterTarget.classList.toggle("docs-chat-counter--warn", len > 449);
    }
    _resizeInput() {
        const ta = this.inputTarget;
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
    }
    // ── Send / stream ─────────────────────────────────────────────────────────
    async send() {
        const question = this.inputTarget.value.trim();
        if (!question || this._loading) return;
        const historySnapshot = this._history.slice(-$9edb7d9b6ecc0921$var$MAX_HISTORY);
        this._hideStarters();
        this._appendMessage("user", question);
        this.inputTarget.value = "";
        this._updateCounter();
        this._resizeInput();
        const assistantEl = this._appendMessage("assistant", "");
        const typingEl = document.createElement("span");
        typingEl.className = "chat-typing";
        typingEl.textContent = "\u2026";
        assistantEl.appendChild(typingEl);
        this._loading = true;
        this.sendTarget.disabled = true;
        this._abortController = new AbortController();
        let assistantText = "";
        try {
            const response = await fetch(this.element.dataset.chatUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    question: question,
                    history: historySnapshot
                }),
                signal: this._abortController.signal
            });
            if (!response.ok) {
                typingEl.remove();
                assistantEl.textContent = response.status === 429 ? "Too many requests. Please wait a moment." : "Something went wrong. Please try again.";
                return;
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let started = false;
            while(true){
                const { done: done, value: value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, {
                    stream: true
                });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const line of lines){
                    if (!line.startsWith("data: ")) continue;
                    const payload = line.slice(6).trim();
                    if (payload === "[DONE]") break;
                    try {
                        const text = JSON.parse(payload);
                        if (!started) {
                            typingEl.remove();
                            started = true;
                        }
                        assistantText += text;
                        // Show plain text while streaming for performance
                        assistantEl.textContent = assistantText;
                        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
                    } catch  {
                    /* ignore parse errors */ }
                }
            }
            if (!started) typingEl.remove();
            // Render markdown and add copy button once the full response is in
            if (assistantText) {
                this._renderMarkdown(assistantEl, assistantText);
                this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
                this._history.push({
                    role: "user",
                    content: question
                });
                this._history.push({
                    role: "assistant",
                    content: assistantText
                });
                if (this._history.length > $9edb7d9b6ecc0921$var$MAX_HISTORY) this._history = this._history.slice(-$9edb7d9b6ecc0921$var$MAX_HISTORY);
                this._sessionMessages.push({
                    role: "user",
                    text: question
                });
                this._sessionMessages.push({
                    role: "assistant",
                    text: assistantText
                });
                this._saveSession();
            }
        } catch (err) {
            if (err.name !== "AbortError") {
                typingEl.remove();
                assistantEl.textContent = "Connection error. Please try again.";
            }
        } finally{
            this._loading = false;
            this.sendTarget.disabled = false;
            this.inputTarget.focus();
        }
    }
    keydown(event) {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            this.send();
        }
    }
    // ── Clear chat ────────────────────────────────────────────────────────────
    clearChat() {
        // Remove all message elements, keeping the starters target in place
        const toRemove = Array.from(this.messagesTarget.children).filter((el)=>el !== this.startersTarget);
        for (const el of toRemove)el.remove();
        this._history = [];
        this._sessionMessages = [];
        this.startersTarget.hidden = false;
        try {
            sessionStorage.removeItem($9edb7d9b6ecc0921$var$SESSION_KEY);
        } catch  {}
    }
    // ── Session persistence ───────────────────────────────────────────────────
    _saveSession() {
        try {
            sessionStorage.setItem($9edb7d9b6ecc0921$var$SESSION_KEY, JSON.stringify({
                history: this._history,
                messages: this._sessionMessages
            }));
        } catch  {}
    }
    _restoreSession() {
        try {
            const raw = sessionStorage.getItem($9edb7d9b6ecc0921$var$SESSION_KEY);
            if (!raw) return;
            const { history: history, messages: messages } = JSON.parse(raw);
            if (Array.isArray(history)) this._history = history;
            if (Array.isArray(messages) && messages.length > 0) {
                this._sessionMessages = messages;
                this._hideStarters();
                for (const { role: role, text: text } of messages){
                    const el = this._appendMessage(role, text);
                    if (role === "assistant") this._renderMarkdown(el, text);
                }
            }
        } catch  {}
    }
    // ── Starters ──────────────────────────────────────────────────────────────
    _hideStarters() {
        this.startersTarget.hidden = true;
    }
    // ── Markdown renderer ─────────────────────────────────────────────────────
    // Builds DOM nodes from text — never uses innerHTML with user-sourced strings.
    _renderMarkdown(el, text) {
        // Clear existing content
        while(el.firstChild)el.firstChild.remove();
        // Auto-close unclosed fenced code block (odd count means truncated response)
        const fenceCount = (text.match(/```/g) || []).length;
        if (fenceCount % 2 !== 0) text += "\n```";
        const frag = document.createDocumentFragment();
        const codeBlockRe = /```(\w*)\n?([\s\S]*?)```/g;
        let lastIndex = 0;
        let match;
        while((match = codeBlockRe.exec(text)) !== null){
            if (match.index > lastIndex) this._appendInline(frag, text.slice(lastIndex, match.index));
            const wrap = document.createElement("div");
            wrap.className = "chat-code-wrap";
            const pre = document.createElement("pre");
            const code = document.createElement("code");
            const codeText = match[2].replace(/\n$/, "");
            code.textContent = codeText;
            pre.appendChild(code);
            wrap.appendChild(pre);
            const copyCode = document.createElement("button");
            copyCode.className = "chat-code-copy";
            copyCode.textContent = "Copy";
            copyCode.setAttribute("aria-label", "Copy code");
            copyCode.addEventListener("click", ()=>{
                navigator.clipboard.writeText(codeText).catch(()=>{});
                copyCode.textContent = "Copied!";
                setTimeout(()=>{
                    copyCode.textContent = "Copy";
                }, 1500);
            });
            wrap.appendChild(copyCode);
            frag.appendChild(wrap);
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) this._appendInline(frag, text.slice(lastIndex));
        // Copy button
        const copyBtn = document.createElement("button");
        copyBtn.className = "chat-message-copy";
        copyBtn.setAttribute("aria-label", "Copy response");
        copyBtn.setAttribute("title", "Copy");
        copyBtn.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
        const rawText = text;
        copyBtn.addEventListener("click", ()=>{
            navigator.clipboard.writeText(rawText).catch(()=>{});
            copyBtn.classList.add("chat-message-copy--copied");
            copyBtn.setAttribute("aria-label", "Copied!");
            setTimeout(()=>{
                copyBtn.classList.remove("chat-message-copy--copied");
                copyBtn.setAttribute("aria-label", "Copy response");
            }, 1500);
        });
        frag.appendChild(copyBtn);
        el.appendChild(frag);
    }
    _appendInline(parent, text) {
        const lines = text.split("\n");
        for(let i = 0; i < lines.length; i++){
            const line = lines[i];
            if (i > 0) parent.appendChild(document.createElement("br"));
            // Heading
            const headingMatch = line.match(/^#{1,3}\s+(.*)/);
            if (headingMatch) {
                const strong = document.createElement("strong");
                strong.className = "chat-heading";
                this._appendFormatted(strong, headingMatch[1]);
                parent.appendChild(strong);
                continue;
            }
            // Unordered list item
            const ulMatch = line.match(/^[-*]\s+(.*)/);
            if (ulMatch) {
                const span = document.createElement("span");
                span.appendChild(document.createTextNode("\u2022 "));
                this._appendFormatted(span, ulMatch[1]);
                parent.appendChild(span);
                continue;
            }
            // Ordered list item
            const olMatch = line.match(/^(\d+)\.\s+(.*)/);
            if (olMatch) {
                const span = document.createElement("span");
                span.appendChild(document.createTextNode(olMatch[1] + ". "));
                this._appendFormatted(span, olMatch[2]);
                parent.appendChild(span);
                continue;
            }
            this._appendFormatted(parent, line);
        }
    }
    _appendFormatted(parent, text) {
        // Handle inline `code`, **bold**, *italic*, [text](url), bare https:// URLs — all via textContent/href, no innerHTML
        const re = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>")\]]+|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
        let last = 0;
        let m;
        while((m = re.exec(text)) !== null){
            if (m.index > last) parent.appendChild(document.createTextNode(text.slice(last, m.index)));
            const chunk = m[0];
            if (chunk.startsWith("[")) {
                const a = document.createElement("a");
                a.href = m[3];
                a.textContent = m[2];
                a.className = "cs-chat-link";
                parent.appendChild(a);
            } else if (chunk.startsWith("http")) {
                const a = document.createElement("a");
                a.href = chunk;
                a.textContent = chunk;
                a.className = "cs-chat-link";
                parent.appendChild(a);
            } else if (chunk.startsWith("`")) {
                const code = document.createElement("code");
                code.textContent = chunk.slice(1, -1);
                parent.appendChild(code);
            } else if (chunk.startsWith("**")) {
                const strong = document.createElement("strong");
                strong.textContent = chunk.slice(2, -2);
                parent.appendChild(strong);
            } else {
                const em = document.createElement("em");
                em.textContent = chunk.slice(1, -1);
                parent.appendChild(em);
            }
            last = m.index + chunk.length;
        }
        if (last < text.length) parent.appendChild(document.createTextNode(text.slice(last)));
    }
    // ── Message factory ───────────────────────────────────────────────────────
    _appendMessage(role, text) {
        const el = document.createElement("div");
        el.className = `chat-message chat-message--${role}`;
        el.textContent = text;
        this.messagesTarget.appendChild(el);
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
        return el;
    }
}



class $b67716cfcb1780d5$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "source"
    ];
    copy(event) {
        event.preventDefault();
        if (!this.hasSourceTarget) return;
        const text = this.sourceTarget.textContent.trim();
        if (!text) return;
        const button = event.currentTarget;
        const label = button.getAttribute("aria-label") || "Copy";
        navigator.clipboard.writeText(text).then(()=>{
            const originalText = button.textContent;
            button.textContent = button.dataset.copyFeedback || "Copied!";
            button.setAttribute("aria-label", "Copied");
            setTimeout(()=>{
                button.textContent = originalText;
                button.setAttribute("aria-label", label);
            }, 2000);
        });
    }
}



class $42ae361d13dd5299$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    toggle(event) {
        const btn = event.currentTarget;
        const groupId = btn.dataset.groupId;
        const isOpen = btn.dataset.open === "true";
        if (isOpen) this._hideGroup(groupId);
        else this._showGroup(groupId);
        btn.dataset.open = isOpen ? "false" : "true";
        this._updateBtn(btn, !isOpen);
    }
    // Show only direct children of this group
    _showGroup(groupId) {
        this.element.querySelectorAll(`[data-parent-group="${groupId}"]`).forEach((row)=>{
            row.hidden = false;
        });
    }
    // Hide all children and recursively collapse any open sub-groups
    _hideGroup(groupId) {
        this.element.querySelectorAll(`[data-parent-group="${groupId}"]`).forEach((row)=>{
            row.hidden = true;
            // Find any nested toggle buttons inside this row and reset them
            row.querySelectorAll("[data-group-id]").forEach((subBtn)=>{
                if (subBtn.dataset.open === "true") {
                    this._hideGroup(subBtn.dataset.groupId);
                    subBtn.dataset.open = "false";
                    this._updateBtn(subBtn, false);
                }
            });
        });
    }
    _updateBtn(btn, open) {
        const label = btn.querySelector(".cs-toggle-label");
        const arrow = btn.querySelector(".cs-toggle-arrow");
        if (label) label.textContent = open ? "Hide child attributes" : "Show child attributes";
        if (arrow) arrow.classList.toggle("cs-toggle-arrow--open", open);
    }
}



class $c939cee082ccd83d$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "list"
    ];
    connect() {
        this._inner = document.querySelector(".docs-content-inner");
        this._sections = this._inner ? Array.from(this._inner.querySelectorAll(":scope > section[id]")) : [];
        if (!this._sections.length) {
            this._hide();
            return;
        }
        this._activeSectionId = null;
        this._headingObserver = null;
        this._visible = new Set();
        this._headings = [];
        this._ticking = false;
        this._boundScroll = this._onScroll.bind(this);
        window.addEventListener("scroll", this._boundScroll, {
            passive: true
        });
        window.addEventListener("resize", this._boundScroll, {
            passive: true
        });
        this._updateActiveSection();
    }
    disconnect() {
        window.removeEventListener("scroll", this._boundScroll);
        window.removeEventListener("resize", this._boundScroll);
        if (this._headingObserver) this._headingObserver.disconnect();
    }
    // ── Active-section tracking ──────────────────────────────────────────────────
    _onScroll() {
        if (this._ticking) return;
        this._ticking = true;
        requestAnimationFrame(()=>{
            this._updateActiveSection();
            this._ticking = false;
        });
    }
    _updateActiveSection() {
        const offset = 100;
        let active = this._sections[0];
        for (const section of this._sections)if (section.getBoundingClientRect().top <= offset) active = section;
        if (!active || active.id === this._activeSectionId) return;
        this._activeSectionId = active.id;
        this._buildRail(active);
    }
    // ── Rail construction ────────────────────────────────────────────────────────
    _buildRail(section) {
        const headings = Array.from(section.querySelectorAll("h2, h3, h4")).filter((h)=>!h.classList.contains("cs-title-main") && !h.classList.contains("cs-title-section") && h.textContent.trim());
        if (this._headingObserver) {
            this._headingObserver.disconnect();
            this._headingObserver = null;
        }
        this._visible.clear();
        this.listTarget.textContent = "";
        if (!headings.length) {
            this._hide();
            this._headings = [];
            return;
        }
        headings.forEach((h)=>{
            if (!h.id) h.id = this._uniqueSlug(h.textContent);
        });
        this._headings = headings;
        const frag = document.createDocumentFragment();
        headings.forEach((h)=>{
            const li = document.createElement("li");
            const a = document.createElement("a");
            a.className = "docs-onpage-link docs-onpage-link--" + h.tagName.toLowerCase();
            a.href = "#" + h.id;
            a.textContent = h.textContent.trim();
            a.dataset.headingId = h.id;
            li.appendChild(a);
            frag.appendChild(li);
        });
        this.listTarget.appendChild(frag);
        this._show();
        this._observeHeadings(headings);
    }
    _observeHeadings(headings) {
        this._headingObserver = new IntersectionObserver((entries)=>{
            for (const entry of entries)if (entry.isIntersecting) this._visible.add(entry.target.id);
            else this._visible.delete(entry.target.id);
            this._highlightCurrent();
        }, {
            rootMargin: "-80px 0px -70% 0px",
            threshold: 0
        });
        headings.forEach((h)=>this._headingObserver.observe(h));
    }
    _highlightCurrent() {
        // Prefer the topmost heading currently in the highlight band; otherwise keep
        // the last heading scrolled above it so the rail always marks a position.
        let currentId = null;
        for (const h of this._headings)if (this._visible.has(h.id)) {
            currentId = h.id;
            break;
        }
        if (!currentId) {
            for (const h of this._headings)if (h.getBoundingClientRect().top <= 100) currentId = h.id;
        }
        this.listTarget.querySelectorAll(".docs-onpage-link").forEach((a)=>{
            a.classList.toggle("is-active", a.dataset.headingId === currentId);
        });
    }
    // ── Helpers ──────────────────────────────────────────────────────────────────
    _uniqueSlug(text) {
        const base = "toc-" + text.trim().toLowerCase().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
        let slug = base || "toc-section";
        let n = 2;
        while(document.getElementById(slug)){
            slug = base + "-" + n;
            n += 1;
        }
        return slug;
    }
    _show() {
        this.element.hidden = false;
    }
    _hide() {
        this.element.hidden = true;
    }
}



class $64778cdc13e824ea$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "input",
        "results"
    ];
    connect() {
        this._index = [];
        this._selectedIdx = -1;
        this._boundClose = this._onOutsideClick.bind(this);
        // Build index after DOM is fully settled
        requestAnimationFrame(()=>{
            this._index = this._buildIndex();
        });
        document.addEventListener("click", this._boundClose);
    }
    disconnect() {
        document.removeEventListener("click", this._boundClose);
    }
    // ── Public Stimulus actions ──────────────────────────────────────────────
    query(event) {
        const q = event.target.value.trim().toLowerCase();
        if (!q) {
            this._hide();
            return;
        }
        const matches = this._index.filter((e)=>e.text.toLowerCase().includes(q) || e.section.toLowerCase().includes(q)).slice(0, 12);
        this._render(matches, q);
    }
    keydown(event) {
        const items = this.resultsTarget.querySelectorAll(".docs-search-result-item");
        if (!items.length) return;
        if (event.key === "ArrowDown") {
            event.preventDefault();
            this._selectedIdx = Math.min(this._selectedIdx + 1, items.length - 1);
            this._updateSelected(items);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            this._selectedIdx = Math.max(this._selectedIdx - 1, 0);
            this._updateSelected(items);
        } else if (event.key === "Enter") {
            if (this._selectedIdx >= 0 && items[this._selectedIdx]) items[this._selectedIdx].click();
        } else if (event.key === "Escape") {
            this._hide();
            this.inputTarget.blur();
        }
    }
    // ── Private ──────────────────────────────────────────────────────────────
    _buildIndex() {
        const entries = [];
        const sidebar = document.querySelector("[data-sidebar-target='nav']");
        if (!sidebar) return entries;
        const sections = sidebar.querySelectorAll("li.docs-sidebar-section");
        sections.forEach((section)=>{
            const sectionTitle = section.querySelector(".docs-sidebar-section-label > .docs-sidebar-label-text")?.textContent?.trim() ?? "";
            // Top-level section heading itself
            if (sectionTitle) {
                const firstLink = section.querySelector("a.docs-sidebar-link");
                if (firstLink) entries.push({
                    text: sectionTitle,
                    href: firstLink.getAttribute("href") || "",
                    section: "Section",
                    isSection: true
                });
            }
            // All links within the section
            section.querySelectorAll("a.docs-sidebar-link").forEach((link)=>{
                const text = this._linkText(link);
                const href = link.getAttribute("href") || "";
                if (text && href.includes("#")) entries.push({
                    text: text,
                    href: href,
                    section: sectionTitle,
                    isSection: false
                });
            });
        });
        return entries;
    }
    // Reads a sidebar link's text. API-reference links are rewritten by
    // SidebarController into <span class="cs-http-badge">POST</span><span
    // class="cs-http-label">Create Wallet</span> with no whitespace between the
    // spans, so textContent alone glues the method to the name ("POSTCreate
    // Wallet"). Join the badge and label with a space when both are present.
    _linkText(link) {
        const badge = link.querySelector(".cs-http-badge");
        const label = link.querySelector(".cs-http-label");
        if (badge && label) return `${badge.textContent.trim()} ${label.textContent.trim()}`.trim();
        return link.textContent.trim();
    }
    _render(matches, q) {
        this._selectedIdx = -1;
        if (!matches.length) {
            this.resultsTarget.innerHTML = `<div class="docs-search-no-results">No results for "<strong>${this._escape(q)}</strong>"</div>`;
            this.resultsTarget.classList.add("open");
            return;
        }
        this.resultsTarget.innerHTML = matches.map((m)=>`
        <a href="${this._escape(m.href)}" class="docs-search-result-item${m.isSection ? " is-section" : ""}">
          <span class="docs-search-result-title">${this._highlight(m.text, q)}</span>
          <span class="docs-search-result-section">${this._escape(m.section)}</span>
        </a>`).join("");
        this.resultsTarget.querySelectorAll(".docs-search-result-item").forEach((item)=>{
            item.addEventListener("click", ()=>{
                this._hide();
                this.inputTarget.value = "";
            });
        });
        this.resultsTarget.classList.add("open");
    }
    _highlight(text, q) {
        const safe = this._escape(text);
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return safe.replace(new RegExp(`(${escaped})`, "gi"), "<mark>$1</mark>");
    }
    _escape(str) {
        return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    }
    _updateSelected(items) {
        items.forEach((item, i)=>{
            item.classList.toggle("selected", i === this._selectedIdx);
            if (i === this._selectedIdx) item.scrollIntoView({
                block: "nearest"
            });
        });
    }
    _hide() {
        if (!this.hasResultsTarget) return;
        this.resultsTarget.innerHTML = "";
        this.resultsTarget.classList.remove("open");
        this._selectedIdx = -1;
    }
    _onOutsideClick(e) {
        if (!this.element.contains(e.target)) this._hide();
    }
}



class $b92fec4591164efa$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "toggle",
        "nav"
    ];
    connect() {
        this._activeId = null;
        this._ticking = false;
        this._boundOnScroll = this._onScroll.bind(this);
        this._boundOnKey = this._onKey.bind(this);
        window.addEventListener("scroll", this._boundOnScroll, {
            passive: true
        });
        window.addEventListener("resize", this._boundOnResize.bind(this), {
            passive: true
        });
        window.addEventListener("keydown", this._boundOnKey);
        this._tagMethodLinks();
        this._sidebarIds = this._collectSidebarIds();
        requestAnimationFrame(()=>{
            const hash = window.location.hash.slice(1).split("?")[0].trim();
            if (hash && document.getElementById(hash)) {
                this._activeId = hash;
                this._highlightSidebarFor(hash);
            } else this._updateActiveSection();
        });
    }
    disconnect() {
        window.removeEventListener("scroll", this._boundOnScroll);
        window.removeEventListener("keydown", this._boundOnKey);
        this._setOpen(false);
    }
    // ── Public actions ────────────────────────────────────────────────────────
    toggle() {
        const isOpen = this.element.classList.contains("sidebar-open");
        this._setOpen(!isOpen);
    }
    close() {
        this._setOpen(false);
    }
    // Close drawer when a nav link is clicked on mobile
    navLinkClick(event) {
        if (this._isMobile() && event.target.closest("a")) this._setOpen(false);
    }
    // ── Private helpers ───────────────────────────────────────────────────────
    _isMobile() {
        return window.innerWidth < 768;
    }
    _setOpen(open) {
        this.element.classList.toggle("sidebar-open", open);
        document.body.classList.toggle("sidebar-open", open);
    }
    _onKey(e) {
        if (e.key === "Escape" && this.element.classList.contains("sidebar-open")) this._setOpen(false);
    }
    _boundOnResize() {
        // Auto-close drawer if viewport grows past mobile breakpoint
        if (!this._isMobile()) this._setOpen(false);
        this._onScroll();
    }
    _tagMethodLinks() {
        if (!this.hasNavTarget) return;
        const methods = [
            "DELETE",
            "PATCH",
            "POST",
            "PUT",
            "GET"
        ];
        this.navTarget.querySelectorAll("a.docs-sidebar-link").forEach((link)=>{
            const text = link.textContent.trim();
            for (const method of methods)if (text.startsWith(method + " ")) {
                const label = text.slice(method.length + 1);
                link.innerHTML = `<span class="cs-http-badge cs-http-badge--${method.toLowerCase()}">${method}</span><span class="cs-http-label">${label}</span>`;
                break;
            }
        });
    }
    _onScroll() {
        if (this._ticking) return;
        this._ticking = true;
        requestAnimationFrame(()=>{
            this._updateActiveSection();
            this._ticking = false;
        });
    }
    // Collect the set of anchor ids that actually appear in the sidebar, so the
    // scroll-spy only ever tracks sections a sidebar link can highlight.
    _collectSidebarIds() {
        const ids = new Set();
        if (!this.hasNavTarget) return ids;
        this.navTarget.querySelectorAll("a.docs-sidebar-link").forEach((link)=>{
            const id = this._getLinkId(link.getAttribute("href") || "");
            if (id) ids.add(id);
        });
        return ids;
    }
    _getSections() {
        const inner = document.querySelector(".docs-content-inner");
        if (!inner) return [];
        const ids = this._sidebarIds || new Set();
        // Only consider sections that have a matching sidebar link. In-content
        // sub-headings (e.g. anchors from the "On this page" rail) have no sidebar
        // entry; including them let the active section advance to an id no link
        // maps to, which left the previous item stuck highlighted and the real one
        // never lit up.
        return Array.from(inner.querySelectorAll("[id]")).filter((el)=>el.id && el.offsetParent !== null && ids.has(el.id));
    }
    _updateActiveSection() {
        const sections = this._getSections();
        if (!sections.length) return;
        const offset = 120;
        let active = sections[0];
        for (const el of sections)if (el.getBoundingClientRect().top <= offset) active = el;
        const id = active.id;
        if (!id || id === this._activeId) return;
        this._activeId = id;
        this._highlightSidebarFor(id);
    }
    _getLinkId(href) {
        if (!href) return "";
        const hash = href.indexOf("#");
        if (hash === -1) return "";
        return href.slice(hash + 1).split("?")[0].replace(/\/$/, "").trim();
    }
    _highlightSidebarFor(id) {
        if (!this.hasNavTarget) return;
        const links = this.navTarget.querySelectorAll("a.docs-sidebar-link");
        const matches = Array.from(links).filter((link)=>this._getLinkId(link.getAttribute("href") || "") === id);
        // No sidebar link maps to this id (e.g. an in-content sub-heading anchor
        // generated by the "On this page" rail) — keep the current highlight rather
        // than clearing it. Accordion open/close state is owned by AccordionController.
        if (!matches.length) return;
        links.forEach((link)=>{
            link.classList.remove("docs-sidebar-link-active");
            link.removeAttribute("aria-current");
        });
        matches.forEach((link)=>{
            link.classList.add("docs-sidebar-link-active");
            link.setAttribute("aria-current", "true");
        });
    }
}



class $468daecd80ba71fe$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "tab",
        "panel"
    ];
    connect() {
        this._activate(0);
    }
    select(event) {
        const index = parseInt(event.currentTarget.dataset.tabIndex, 10);
        this._activate(index);
    }
    _activate(index) {
        this.tabTargets.forEach((tab, i)=>{
            tab.classList.toggle("cs-tab-active", i === index);
        });
        this.panelTargets.forEach((panel, i)=>{
            panel.hidden = i !== index;
        });
    }
}



const $40b2017cce2267b7$var$STORAGE_KEY = "docs-theme";
const $40b2017cce2267b7$var$MOBILE_BREAKPOINT = 768;
class $40b2017cce2267b7$export$2e2bcd8739ae039 extends (0, $ffdf60ebf0144301$export$bd0bf19f25da8474) {
    static targets = [
        "toggle",
        "logoLight",
        "logoDark"
    ];
    connect() {
        this._applyForViewport();
        this._mq = window.matchMedia(`(max-width: ${$40b2017cce2267b7$var$MOBILE_BREAKPOINT - 1}px)`);
        this._boundMqChange = ()=>this._applyForViewport();
        this._mq.addEventListener("change", this._boundMqChange);
    }
    disconnect() {
        this._mq?.removeEventListener("change", this._boundMqChange);
    }
    toggle() {
        const isDark = document.documentElement.classList.contains("dark");
        this.apply(!isDark);
    }
    apply(dark) {
        if (dark) {
            document.documentElement.classList.add("dark");
            localStorage.setItem($40b2017cce2267b7$var$STORAGE_KEY, "dark");
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem($40b2017cce2267b7$var$STORAGE_KEY, "light");
        }
    }
    _isMobile() {
        return window.innerWidth < $40b2017cce2267b7$var$MOBILE_BREAKPOINT;
    }
    _applyForViewport() {
        if (this._isMobile()) {
            // Mobile is always dark — don't touch stored preference
            document.documentElement.classList.add("dark");
            return;
        }
        const saved = localStorage.getItem($40b2017cce2267b7$var$STORAGE_KEY);
        // Default is dark; only go light if the user has explicitly chosen light
        const dark = saved !== "light";
        this.apply(dark);
    }
}


var $d83c1e174dba8e1d$export$2e2bcd8739ae039 = [
    {
        name: "AccordionController",
        controller: (0, $6d9413c31e161bfa$export$2e2bcd8739ae039)
    },
    {
        name: "ChatController",
        controller: (0, $9edb7d9b6ecc0921$export$2e2bcd8739ae039)
    },
    {
        name: "CopyController",
        controller: (0, $b67716cfcb1780d5$export$2e2bcd8739ae039)
    },
    {
        name: "ExpandController",
        controller: (0, $42ae361d13dd5299$export$2e2bcd8739ae039)
    },
    {
        name: "OnpageController",
        controller: (0, $c939cee082ccd83d$export$2e2bcd8739ae039)
    },
    {
        name: "SearchController",
        controller: (0, $64778cdc13e824ea$export$2e2bcd8739ae039)
    },
    {
        name: "SidebarController",
        controller: (0, $b92fec4591164efa$export$2e2bcd8739ae039)
    },
    {
        name: "TabsController",
        controller: (0, $468daecd80ba71fe$export$2e2bcd8739ae039)
    },
    {
        name: "ThemeController",
        controller: (0, $40b2017cce2267b7$export$2e2bcd8739ae039)
    }
];


const $42031045440765e6$var$application = (0, $ffdf60ebf0144301$export$16975c34e60e1e61).start();
$42031045440765e6$var$application.debug = false;
function $42031045440765e6$var$toIdentifier(segment) {
    return segment.replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/([A-Z])([A-Z][a-z])/g, "$1-$2").replace(/_/g, "-").toLowerCase();
}
for (const { name: name, controller: controller } of (0, $d83c1e174dba8e1d$export$2e2bcd8739ae039)){
    const cleanName = name.replace("Controller", "").replace(/_/g, "-");
    const identifier = $42031045440765e6$var$toIdentifier(cleanName);
    $42031045440765e6$var$application.register(identifier, controller);
}

})();
//# sourceMappingURL=app.js.map
